// ----------------------------------------------------------------------------
// xtracker — old-school sample tracker (MOD/XM) SPA
// ----------------------------------------------------------------------------
// Layout: transport (top) + main(left-panel | pattern-editor) + sample-editor (bottom) + bucket (right)
//
// Engine: ported from BassoonTracker (tracker.js, audio.js, editor.js, fileformats)
// UI: fresh HTML/CSS grid (not canvas), themed with azathoth --c0..--cf
//
// Features:
//   - Load / play / edit Protracker MOD & FastTracker XM
//   - Pattern editor grid with keyboard note entry (tracker-style)
//   - Instrument list + sample slots
//   - Drag & drop samples from bucket → sample slots
//   - Song sequencer (pattern order list)
//   - Sample editor (properties + waveform)
//   - Export to WAV (offline render) + save MOD/XM
// ----------------------------------------------------------------------------

import initLegacyBucket from './legacy-bucket-integration.js';
import { decodeAIFF, isAIFF } from './aiff-decoder.js';
import SplitModule from 'split-grid';
const Split = SplitModule.default || SplitModule;

// ---- Engine (ESM modules bundled by esbuild) ----
import Tracker, { FTNotes, periodNoteTable, noteNames } from './xtracker/engine/tracker.js';
import Audio from './xtracker/engine/audio.js';
import Editor from './xtracker/engine/editor.js';
import EventBus from './xtracker/engine/eventBus.js';
import { EVENT, NOTEOFF, KEYBOARDKEYS, KEYBOARDTABLE, TRACKERMODE, MODULETYPE } from './xtracker/engine/enum.js';
import Note from './xtracker/engine/models/note.js';
import Sample from './xtracker/engine/models/sample.js';
import Instrument from './xtracker/engine/models/instrument.js';
import { BinaryStream } from './xtracker/engine/filesystem.js';
import { audioBufferToWav } from './xtracker/engine/lib/audioBufferToWav.js';
import { initSampleEditor, loadSample, redraw as redrawSampleEditor, onEngineSamplePlay as seOnEngineSamplePlay } from './xtracker/sampleEditor.js';
import { initInstrumentEditor, loadInstrument, syncUI as syncInstEditorUI, draw as drawInstEditor } from './xtracker/instrumentEditor.js';
import midiPanel from './xtracker/midiPanel.js';

// ---- Theme setup (same pattern as dawseur/xtrc/patch) ----
const storedTheme = localStorage.getItem('currentTheme');
const currentTheme = storedTheme ? JSON.parse(storedTheme) : window.Theme;
window.themeColors = Object.values(currentTheme.data);
window.currentTheme = currentTheme;
window.CONTROLZ = {};

// ---- State ----
let bucket = null;
let exportModal = null;
let audioCtx = null;
let cursor = { track: 0, row: 0, col: 0 }; // col: 0=note,1=ins,2=vol,3=fx,4=param
// Selection: { startRow, endRow, startTrack, endTrack } or null
let selection = null;
let isSelecting = false;
let selectionAnchor = null; // { track, row } for shift-click / shift-arrow
// Clipboard: array of { track, row, note } (deep-copied note objects)
let patternClipboard = null;
// Undo/redo history for pattern edits
let undoStack = [];
let redoStack = [];
const UNDO_LIMIT = 50;

function pushUndo() {
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const snapshot = pattern.map(row => {
      if (!row) return null;
      return row.map(note => note ? cloneNote(note) : undefined);
   });
   undoStack.push(snapshot);
   if (undoStack.length > UNDO_LIMIT) undoStack.shift();
   redoStack = [];
}

function doUndo() {
   if (undoStack.length === 0) return;
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const current = pattern.map(row => {
      if (!row) return null;
      return row.map(note => note ? cloneNote(note) : undefined);
   });
   redoStack.push(current);
   const snapshot = undoStack.pop();
   pattern.length = 0;
   for (const row of snapshot) {
      pattern.push(row ? row.map(note => note ? cloneNote(note) : undefined) : []);
   }
   renderPattern();
}

function doRedo() {
   if (redoStack.length === 0) return;
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const current = pattern.map(row => {
      if (!row) return null;
      return row.map(note => note ? cloneNote(note) : undefined);
   });
   undoStack.push(current);
   const snapshot = redoStack.pop();
   pattern.length = 0;
   for (const row of snapshot) {
      pattern.push(row ? row.map(note => note ? cloneNote(note) : undefined) : []);
   }
   renderPattern();
}

// Hex entry buffer for ins/vol/fx/param columns
let hexBuffer = '';
let hexBufferTimer = null;
let currentSampleSlot = 0;
let isPlaying = false;
let playRow = -1;
let playSongPos = -1;
let playheadRAF = null;
let lastPlayRow = -1;
let lastPlaySongPos = -1;
let renderRequested = false;
let editMode = false;
let recordMode = false; // default to playback mode

// ---- DOM refs ----
const $ = (id) => document.getElementById(id);
const dom = {
   playBtn: $('xt-play'),
   recordBtn: $('xt-record'),
   bpmInput: $('xt-bpm'),
   speedInput: $('xt-speed'),
   octaveInput: $('xt-octave'),
   addInput: $('xt-add'),
   playmodeSelect: $('xt-playmode'),
   formatSelect: $('xt-format'),
   modeEditBtn: $('xt-mode-edit'),
   positionDisplay: $('xt-position'),
   loadBtn: $('xt-load'),
   saveBtn: $('xt-save'),
   exportBtn: $('xt-export'),
   newBtn: $('xt-new'),
   fileInput: $('xt-file-input'),
   // pattern
   patternNum: $('xt-pattern-num'),
   patternLen: $('xt-pattern-len'),
   trackCount: $('xt-track-count'),
   ptClear: $('xt-pt-clear'),
   ptCopy: $('xt-pt-copy'),
   ptPaste: $('xt-pt-paste'),
   patternScroll: $('xt-pattern-scroll'),
   patternGrid: $('xt-pattern-grid'),
   // left panel
   instrList: $('xt-instr-list'),
   addInstr: $('xt-add-instr'),
   sampleSlots: $('xt-sample-slots'),
   clearSample: $('xt-clear-sample'),
   songSeq: $('xt-song-seq'),
   addPos: $('xt-add-pos'),
   // sample editor
   seName: $('xt-se-name'),
   seVol: $('xt-se-vol'),
   seFinetune: $('xt-se-finetune'),
   sePan: $('xt-se-pan'),
   seRelnote: $('xt-se-relnote'),
   seC2spd: $('xt-se-c2spd'),
   seLoop: $('xt-se-loop'),
   seLoopstart: $('xt-se-loopstart'),
   seLooplen: $('xt-se-looplen'),
   seWave: $('xt-se-wave'),
   // module search
   msSearch: $('xt-ms-search'),
   msSortby: $('xt-ms-sortby'),
   msResults: $('xt-ms-results'),
};

// ============================================================================
// ENGINE INIT
// ============================================================================
function initEngine() {
   Tracker.init();
   // Create AudioContext at startup (BassoonTracker does the same).
   // Browsers may block it from starting until a user gesture, but the
   // graph nodes (masterVolume etc.) must exist before Tracker.new()
   // calls resetDefaultSettings() -> Audio.setMasterVolume().
   // It will resume on the first play/ensureAudio() call.
   const AC = window.AudioContext || window.webkitAudioContext;
   if (AC) {
      audioCtx = new AC();
      Audio.init(audioCtx, null);
   }
}

function ensureAudio() {
   if (audioCtx) {
      if (audioCtx.state === 'suspended') audioCtx.resume();
      return audioCtx;
   }
   const AC = window.AudioContext || window.webkitAudioContext;
   audioCtx = new AC();
   Audio.init(audioCtx, null);
   return audioCtx;
}

// ============================================================================
// NOTE HELPERS
// ============================================================================
function getNoteString(note) {
   if (!note) return '---';
   if (note.index === NOTEOFF || note.period === NOTEOFF) return '===';
   if (note.index) {
      const ft = FTNotes[note.index];
      if (ft) return ft.name;
   }
   if (Tracker.inFTMode()) {
      return '---';
   } else {
      const base = periodNoteTable[note.period];
      return base ? base.name : '---';
   }
}

function hex2(n) {
   if (!n) return '..';
   return n.toString(16).toUpperCase().padStart(2, '0');
}

function getVolString(note) {
   if (!note || !note.volumeEffect) return '..';
   let v = note.volumeEffect;
   if (v < 16) return '..';
   v -= 16;
   if (v < 80) return hex2(v);
   const hi = (v >> 4).toString(16).toUpperCase();
   const lo = (v & 0x0f).toString(16).toUpperCase();
   const map = { '5': '-', '6': '+', '7': '\u2193', '8': '\u2191', '9': 'S', 'A': 'V', 'B': 'P', 'C': '<', 'D': '>', 'E': 'M' };
   return (map[hi] || hi) + lo;
}

function getFxString(note) {
   if (!note || !note.effect) return '.';
   return note.effect.toString(16).toUpperCase();
}

function getParamString(note) {
   if (!note || (!note.effect && !note.param)) return '..';
   return hex2(note.param);
}

// Convert a KEYBOARDKEYS value (1-27) to a note index for the engine
function keyToNoteIndex(keyVal) {
   // KEYBOARDKEYS: 1-12 = C..B (base octave), 13-24 = C..B (+1 octave), 25-27 = C..D (+2)
   // FTNotes table: index 1 = C-0, 12 = B-0, 13 = C-1, etc.
   // So noteIndex = keyVal directly maps to FTNotes index (1-based)
   // But we need to offset by octave. The base octave is determined by Input.getCurrentOctave()
   // In bassoontracker, the octave shifts the note. FTNotes has 0-95 (C-0 to B-7).
   // keyVal 1-12 = octave, 13-24 = octave+1, 25-27 = octave+2
   const octave = parseInt(dom.octaveInput.value, 10) || 4;
   let noteIndex;
   if (keyVal <= 12) noteIndex = (octave) * 12 + keyVal;
   else if (keyVal <= 24) noteIndex = (octave + 1) * 12 + (keyVal - 12);
   else noteIndex = (octave + 2) * 12 + (keyVal - 24);
   return noteIndex;
}

// ============================================================================
// PATTERN RENDERING
// ============================================================================
function renderPattern() {
   if (renderRequested) return;
   renderRequested = true;
   requestAnimationFrame(() => {
      renderRequested = false;
      renderPatternNow();
   });
}

function isInSelection(track, row) {
   if (!selection) return false;
   const t0 = Math.min(selection.startTrack, selection.endTrack);
   const t1 = Math.max(selection.startTrack, selection.endTrack);
   const r0 = Math.min(selection.startRow, selection.endRow);
   const r1 = Math.max(selection.startRow, selection.endRow);
   return track >= t0 && track <= t1 && row >= r0 && row <= r1;
}

function renderPatternNow() {
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const len = Tracker.getPatternLength();
   const tracks = Tracker.getTrackCount();
   const grid = dom.patternGrid;
   grid.innerHTML = '';

   const frag = document.createDocumentFragment();
   for (let row = 0; row < len; row++) {
      const rowEl = document.createElement('div');
      rowEl.className = 'xt-row';
      const isBeat = row % 4 === 0;
      if (isBeat) rowEl.classList.add('xt-row-beat');
      if (editMode && row === cursor.row) rowEl.classList.add('xt-row-cursor');
      if (row === playRow && isPlaying) rowEl.classList.add('xt-row-playing');

      // row number
      const numEl = document.createElement('div');
      numEl.className = 'xt-row-num';
      if (isBeat) numEl.classList.add('xt-row-num-beat');
      if (row === playRow && isPlaying) numEl.classList.add('xt-row-num-playing');
      numEl.textContent = row.toString(16).toUpperCase().padStart(2, '0');
      rowEl.appendChild(numEl);

      // cells
      for (let t = 0; t < tracks; t++) {
         const note = pattern[row] && pattern[row][t];
         const cell = document.createElement('div');
         cell.className = 'xt-cell';
         cell.dataset.track = t;
         cell.dataset.row = row;
         if (isBeat) cell.classList.add('xt-cell-beat');
         const isCursor = editMode && (t === cursor.track && row === cursor.row);
         if (isCursor) cell.classList.add('xt-cell-cursor');
         if (editMode && isInSelection(t, row) && !isCursor) cell.classList.add('xt-cell-selected');

         const noteStr = getNoteString(note);
         const ne = document.createElement('span');
         ne.className = 'xt-cell-note';
         if (noteStr === '---') ne.classList.add('xt-note-empty');
         else if (noteStr === '===') ne.classList.add('xt-note-off');
         if (isCursor && cursor.col === 0) ne.classList.add('xt-sub-cursor');
         ne.textContent = noteStr;
         cell.appendChild(ne);

         const ins = document.createElement('span');
         ins.className = 'xt-cell-ins';
         if (!note || !note.instrument) ins.classList.add('xt-ins-empty');
         if (isCursor && cursor.col === 1) ins.classList.add('xt-sub-cursor');
         ins.textContent = note && note.instrument ? hex2(note.instrument) : '..';
         cell.appendChild(ins);

         const vol = document.createElement('span');
         vol.className = 'xt-cell-vol';
         const vs = getVolString(note);
         if (vs === '..') vol.classList.add('xt-vol-empty');
         if (isCursor && cursor.col === 2) vol.classList.add('xt-sub-cursor');
         vol.textContent = vs;
         cell.appendChild(vol);

         const fx = document.createElement('span');
         fx.className = 'xt-cell-fx';
         const fs = getFxString(note);
         if (fs === '.') fx.classList.add('xt-fx-empty');
         if (isCursor && cursor.col === 3) fx.classList.add('xt-sub-cursor');
         fx.textContent = fs;
         cell.appendChild(fx);

         const param = document.createElement('span');
         param.className = 'xt-cell-param';
         const ps = getParamString(note);
         if (ps === '..') param.classList.add('xt-param-empty');
         if (isCursor && cursor.col === 4) param.classList.add('xt-sub-cursor');
         param.textContent = ps;
         cell.appendChild(param);

         rowEl.appendChild(cell);
      }
      frag.appendChild(rowEl);
   }
   grid.appendChild(frag);
   renderChannelHeaders();
   scrollCursorIntoView();
}

function scrollCursorIntoView() {
   const scroll = dom.patternScroll;
   const rowH = 18;
   const cursorY = cursor.row * rowH;
   const viewTop = scroll.scrollTop;
   const viewH = scroll.clientHeight;
   if (cursorY < viewTop + 40) scroll.scrollTop = Math.max(0, cursorY - 40);
   else if (cursorY > viewTop + viewH - 60) scroll.scrollTop = cursorY - viewH + 60;
}

function scrollPlayheadIntoView() {
   if (playRow < 0) return;
   const scroll = dom.patternScroll;
   if (!scroll) return;
   const rowH = 18;
   const playY = playRow * rowH;
   const viewTop = scroll.scrollTop;
   const viewH = scroll.clientHeight;
   const margin = Math.floor(viewH * 0.3); // 30% margin top/bottom
   if (playY < viewTop + margin) {
      scroll.scrollTop = Math.max(0, playY - margin);
   } else if (playY > viewTop + viewH - margin) {
      scroll.scrollTop = playY - viewH + margin;
   }
}

// Lightweight playhead update — just toggle classes on existing rows, no DOM rebuild
function updatePlayheadHighlight() {
   const grid = dom.patternGrid;
   if (!grid) return;
   const rows = grid.children;
   for (let i = 0; i < rows.length; i++) {
      const rowEl = rows[i];
      if (!rowEl.classList || !rowEl.classList.contains('xt-row')) continue;
      // Determine row index from the row number element
      const numEl = rowEl.querySelector('.xt-row-num');
      if (!numEl) continue;
      const rowIdx = parseInt(numEl.textContent, 16);
      const isPlayingRow = (rowIdx === playRow && isPlaying);
      rowEl.classList.toggle('xt-row-playing', isPlayingRow);
      numEl.classList.toggle('xt-row-num-playing', isPlayingRow);
   }
}

// ============================================================================
// INSTRUMENT LIST
// ============================================================================
function renderInstruments() {
   const instruments = Tracker.getInstruments();
   const current = Tracker.getCurrentInstrumentIndex();
   const list = dom.instrList;
   list.innerHTML = '';
   const frag = document.createDocumentFragment();
   for (let i = 1; i < instruments.length; i++) {
      const inst = instruments[i];
      if (!inst) continue;
      const el = document.createElement('div');
      el.className = 'xt-instr-item';
      if (i === current) el.classList.add('selected');
      const num = document.createElement('span');
      num.className = 'xt-instr-num';
      num.textContent = hex2(i);
      const name = document.createElement('span');
      name.className = 'xt-instr-name';
      name.textContent = inst.name || ('Instrument ' + i);
      el.appendChild(num);
      el.appendChild(name);
      el.addEventListener('click', () => {
         Tracker.setCurrentInstrumentIndex(i);
         renderInstruments();
         renderSampleSlots();
         renderSampleEditor();
      });
      frag.appendChild(el);
   }
   list.appendChild(frag);
}

// ============================================================================
// SAMPLE SLOTS (drag-drop from bucket)
// ============================================================================
function renderSampleSlots() {
   const inst = Tracker.getCurrentInstrument();
   const slots = dom.sampleSlots;
   slots.innerHTML = '';
   if (!inst) return;
   const samples = inst.samples || [inst.sample];
   const frag = document.createDocumentFragment();
   for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      const el = document.createElement('div');
      el.className = 'xt-sample-slot';
      if (i === currentSampleSlot) el.classList.add('selected');
      if (!s || !s.length) el.classList.add('empty');
      el.draggable = false;
      el.dataset.slot = i;

      const num = document.createElement('span');
      num.className = 'xt-slot-num';
      num.textContent = hex2(i);
      const name = document.createElement('span');
      name.className = 'xt-slot-name';
      name.textContent = (s && s.name) ? s.name : (s && s.length ? '(unnamed)' : 'empty');
      const len = document.createElement('span');
      len.className = 'xt-slot-len';
      len.textContent = s && s.length ? (s.length + ' sm') : '';
      el.appendChild(num);
      el.appendChild(name);
      el.appendChild(len);

      el.addEventListener('click', () => {
         currentSampleSlot = i;
         if (inst.sampleIndex !== i) inst.sampleIndex = i;
         inst.sample = inst.samples[i];
         renderSampleSlots();
         renderSampleEditor();
      });

      // ---- drag & drop from bucket / sample search ----
      el.addEventListener('dragover', (e) => {
         e.preventDefault();
         e.stopPropagation();
         if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
         el.classList.add('dragover');
      });
      el.addEventListener('dragleave', (e) => {
         if (e.target === el) el.classList.remove('dragover');
      });
      el.addEventListener('drop', (e) => {
         e.preventDefault();
         e.stopPropagation();
         el.classList.remove('dragover');
         handleSampleDrop(e, i);
      });
      // Prevent child spans from blocking drag events
      [num, name, len].forEach(child => {
         child.style.pointerEvents = 'none';
      });

      frag.appendChild(el);
   }
   slots.appendChild(frag);
}

async function handleSampleDrop(e, slotIndex) {
   // 1. Bucket items drag with application/x-bucket-item-id
   const bucketItemId = e.dataTransfer.getData('application/x-bucket-item-id');
   if (bucketItemId && bucket) {
      const item = bucket.items.find(it => it.id === bucketItemId);
      if (item) {
         let ab;
         if (item.wav instanceof ArrayBuffer) ab = item.wav;
         else if (item.wav instanceof Uint8Array) ab = item.wav.buffer;
         else if (item.wavLike instanceof ArrayBuffer) ab = item.wavLike;
         else if (item.wavLike instanceof Uint8Array) ab = item.wavLike.buffer;
         else if (item.data instanceof ArrayBuffer) ab = item.data;
         else if (item.data instanceof Uint8Array) ab = item.data.buffer;
         else { console.warn('[xtracker] no audio data in bucket item', item); return; }
         importSampleFromArrayBuffer(ab.slice(0), item.name || 'sample', slotIndex);
         return;
      }
   }
   // 2. Samplebank JSON from sample search panel
   const raw = e.dataTransfer.getData('text/plain');
   if (raw) {
      try {
         const parsed = JSON.parse(raw);
         if (parsed && parsed.type === 'samplebank' && (parsed.uuid || parsed.hash || parsed.filename)) {
            const id = parsed.uuid || parsed.hash || parsed.filename;
            const name = parsed.filename || parsed.name || 'sample';
            const url = '/media/get/' + encodeURIComponent(id) + '?format=samplebank';
            const resp = await fetch(url);
            if (resp.ok) {
               const ab = await resp.arrayBuffer();
               importSampleFromArrayBuffer(ab, name, slotIndex);
               return;
            }
         }
      } catch (_) {}
   }
   // 3. OS file drop
   if (e.dataTransfer.files && e.dataTransfer.files.length) {
      const f = e.dataTransfer.files[0];
      const ab = await f.arrayBuffer();
      importSampleFromArrayBuffer(ab, f.name, slotIndex);
   }
}

function importSampleFromArrayBuffer(arrayBuffer, name, slotIndex) {
   ensureAudio();
   const inst = Tracker.getCurrentInstrument();
   if (!inst) return;
   // Try native decodeAudioData first, fall back to AIFF parser
   audioCtx.decodeAudioData(arrayBuffer.slice(0)).then(buffer => {
      if (!buffer) { console.warn('[xtracker] could not decode sample'); return; }
      finishSampleImport(buffer, name, slotIndex, inst);
   }).catch(() => {
      // AIFF fallback (Chrome/Firefox don't support AIFF in decodeAudioData)
      if (isAIFF(arrayBuffer)) {
         decodeAIFF(audioCtx, arrayBuffer).then(buffer => {
            if (!buffer) { tryRawImport(arrayBuffer, name); return; }
            finishSampleImport(buffer, name, slotIndex, inst);
         }).catch(() => tryRawImport(arrayBuffer, name));
      } else {
         tryRawImport(arrayBuffer, name);
      }
   });
}

function finishSampleImport(buffer, name, slotIndex, inst) {
   const data = audioBufferToSampleData(buffer);
   const samples = inst.samples || [inst.sample];
   while (samples.length <= slotIndex) {
      samples.push(Sample());
   }
   const s = samples[slotIndex];
   s.data = data;
   s.length = data.length;
   s.name = (name || 'sample').replace(/\.[^.]+$/, '').slice(0, 22);
   s.bits = 8;
   inst.samples = samples;
   inst.sample = s;
   inst.sampleIndex = slotIndex;
   currentSampleSlot = slotIndex;
   renderSampleSlots();
   renderSampleEditor();
   renderInstruments();
}

function tryRawImport(arrayBuffer, name) {
   console.warn('[xtracker] trying raw import for', name);
   try {
      const file = new BinaryStream(arrayBuffer);
      Editor.importSample(file, name);
      renderSampleSlots();
      renderSampleEditor();
   } catch (e2) {
      console.error('[xtracker] sample import failed', e2);
   }
}

function audioBufferToSampleData(buffer) {
   // Mix down to mono, convert to float -1..1 sample data (bassoon engine uses floats)
   const channels = buffer.numberOfChannels;
   const len = buffer.length;
   const out = new Float32Array(len);
   for (let c = 0; c < channels; c++) {
      const ch = buffer.getChannelData(c);
      for (let i = 0; i < len; i++) out[i] += ch[i] / channels;
   }
   return Array.from(out);
}

// ============================================================================
// SONG SEQUENCER
// ============================================================================
function renderSongSeq() {
   const song = Tracker.getSong();
   const seq = dom.songSeq;
   seq.innerHTML = '';
   if (!song || !song.patternTable) return;
   const frag = document.createDocumentFragment();
   const maxPtn = (Tracker.getPatternCount ? Tracker.getPatternCount() : 256) - 1;
   const songLen = Math.min(song.length || song.patternTable.length, song.patternTable.length);
   for (let i = 0; i < songLen; i++) {
      if (song.patternTable[i] === undefined) continue;
      const el = document.createElement('div');
      el.className = 'xt-seq-item';
      if (i === playSongPos && isPlaying) el.classList.add('playing');
      const pos = document.createElement('span');
      pos.className = 'xt-seq-pos';
      pos.textContent = i.toString(16).toUpperCase().padStart(2, '0');
      // Decrement pattern number
      const dec = document.createElement('span');
      dec.className = 'xt-seq-btn xt-seq-dec';
      dec.textContent = '\u2013';
      dec.title = 'Decrement pattern';
      dec.addEventListener('click', (e) => {
         e.stopPropagation();
         const v = song.patternTable[i];
         if (v > 0) { Tracker.updatePatternTable(i, v - 1); renderSongSeq(); }
      });
      const ptn = document.createElement('span');
      ptn.className = 'xt-seq-ptn';
      ptn.textContent = song.patternTable[i].toString(16).toUpperCase().padStart(2, '0');
      // Increment pattern number
      const inc = document.createElement('span');
      inc.className = 'xt-seq-btn xt-seq-inc';
      inc.textContent = '+';
      inc.title = 'Increment pattern';
      inc.addEventListener('click', (e) => {
         e.stopPropagation();
         const v = song.patternTable[i];
         if (v < maxPtn) { Tracker.updatePatternTable(i, v + 1); renderSongSeq(); }
      });
      el.appendChild(pos);
      el.appendChild(dec);
      el.appendChild(ptn);
      el.appendChild(inc);
      el.addEventListener('click', () => {
         Tracker.setCurrentSongPosition(i, true);
         renderSongSeq();
      });
      const del = document.createElement('span');
      del.className = 'xt-seq-del';
      del.textContent = '\u2715';
      del.addEventListener('click', (e) => {
         e.stopPropagation();
         Editor.removeFromPatternTable && Editor.removeFromPatternTable(i);
         renderSongSeq();
      });
      el.appendChild(del);
      frag.appendChild(el);
   }
   seq.appendChild(frag);
}

// ============================================================================
// SAMPLE EDITOR
// ============================================================================
function renderSampleEditor() {
   const inst = Tracker.getCurrentInstrument();
   if (!inst || !inst.sample) {
      loadSample(null);
      return;
   }
   const s = inst.sample;
   dom.seName.value = s.name || '';
   dom.seVol.value = s.volume != null ? s.volume : 64;
   dom.seFinetune.value = s.finetune != null ? s.finetune : 0;
   dom.sePan.value = s.panning != null ? s.panning : 128;
   dom.seRelnote.value = s.relativeNote != null ? s.relativeNote : 0;
   dom.seC2spd.value = s.c2spd != null ? s.c2spd : 8363;
   dom.seLoop.checked = !!(s.loop && s.loop.enabled);
   dom.seLoopstart.value = s.loop ? s.loop.start : 0;
   dom.seLooplen.value = s.loop ? s.loop.length : 0;
   loadSample(s);
}

// ============================================================================
// TRANSPORT / POSITION
// ============================================================================
function updatePosition() {
   const row = Tracker.getCurrentPatternPos();
   const songPos = Tracker.getCurrentSongPosition();
   const r = row.toString(16).toUpperCase().padStart(2, '0');
   const s = songPos.toString(16).toUpperCase().padStart(2, '0');
   dom.positionDisplay.textContent = s + ' / ' + r;
}

function updateTransport() {
   dom.bpmInput.value = Tracker.getBPM();
   dom.speedInput.value = Tracker.getAmigaSpeed();
   dom.patternNum.value = Tracker.getCurrentPattern();
   dom.patternLen.value = Tracker.getPatternLength();
   dom.trackCount.value = Tracker.getTrackCount();
   const mode = Tracker.inFTMode() ? 'xm' : 'mod';
   dom.formatSelect.value = mode;
   updatePosition();
}

// ============================================================================
// PLAYHEAD — real-time animation via requestAnimationFrame
// The engine uses look-ahead scheduling, so events fire in batches.
// We poll getStateAtTime(Audio.context.currentTime) for the actual playing step.
// ============================================================================
function startPlayhead() {
   if (playheadRAF) return;
   lastPlayRow = -1;
   lastPlaySongPos = -1;
   const tick = () => {
      if (!isPlaying) { stopPlayhead(); return; }
      simulateChannelWaves();
      // In record mode, cursor follows playhead
      if (recordMode && playRow >= 0) {
         setCursor(cursor.track, playRow, cursor.col);
         updatePlayheadHighlight();
      }
      const ctx = Audio.context;
      if (ctx && Tracker.getStateAtTime) {
         const state = Tracker.getStateAtTime(ctx.currentTime);
         if (state) {
            const newRow = state.patternPos != null ? state.patternPos : -1;
            const newSongPos = state.songPos != null ? state.songPos : -1;
            if (newRow !== lastPlayRow || newSongPos !== lastPlaySongPos) {
               lastPlayRow = newRow;
               lastPlaySongPos = newSongPos;
               playRow = newRow;
               let patternChanged = false;
               if (newSongPos !== playSongPos) {
                  playSongPos = newSongPos;
                  // Switch pattern if song position changed
                  const song = Tracker.getSong();
                  if (song && song.patternTable && newSongPos < song.patternTable.length) {
                     const ptn = song.patternTable[newSongPos];
                     if (ptn != null && ptn !== Tracker.getCurrentPattern()) {
                        Tracker.setCurrentPattern(ptn);
                        patternChanged = true;
                     }
                  }
                  renderSongSeq();
               }
               updatePosition();
               if (patternChanged) {
                  renderPattern();
                  requestAnimationFrame(() => updatePlayheadHighlight());
               } else {
                  updatePlayheadHighlight();
               }
               scrollPlayheadIntoView();
            }
         }
      }
      playheadRAF = requestAnimationFrame(tick);
   };
   playheadRAF = requestAnimationFrame(tick);
}

function stopPlayhead() {
   if (playheadRAF) { cancelAnimationFrame(playheadRAF); playheadRAF = null; }
   lastPlayRow = -1;
   lastPlaySongPos = -1;
}

// ============================================================================
// EVENT BUS — listen to engine events
// ============================================================================
function wireEngineEvents() {
   EventBus.on(EVENT.patternPosChange, (pos) => {
      playRow = (pos && pos.current != null) ? pos.current : Tracker.getCurrentPatternPos();
      updatePosition();
      renderPattern();
   });
   EventBus.on(EVENT.songPositionChange, () => {
      playSongPos = Tracker.getCurrentSongPosition();
      updatePosition();
      renderSongSeq();
   });
   EventBus.on(EVENT.playingChange, (playing) => {
      isPlaying = !!playing;
      dom.playBtn.classList.toggle('active', isPlaying);
      dom.playBtn.innerHTML = isPlaying ? '\uE801' : '\uE800';
      if (!isPlaying) { playRow = -1; playSongPos = -1; stopPlayhead(); midiPanel.stopAllMIDI(); }
      else { startPlayhead(); }
      renderPattern();
      renderSongSeq();
   });
   EventBus.on(EVENT.patternChange, () => {
      dom.patternNum.value = Tracker.getCurrentPattern();
      dom.patternLen.value = Tracker.getPatternLength();
      cursor.row = 0;
      renderPattern();
   });
   EventBus.on(EVENT.songBPMChange, () => { dom.bpmInput.value = Tracker.getBPM(); });
   EventBus.on(EVENT.songSpeedChange, () => { dom.speedInput.value = Tracker.getAmigaSpeed(); });
   EventBus.on(EVENT.instrumentChange, () => { renderInstruments(); renderSampleSlots(); renderSampleEditor(); });
   EventBus.on(EVENT.instrumentListChange, () => { renderInstruments(); renderSampleSlots(); });
   // Forward engine sample-play events to the sample editor for playhead display
   EventBus.on(EVENT.samplePlay, (result) => {
      if (!result || result.instrumentIndex == null) return;
      if (result.instrumentIndex !== Tracker.getCurrentInstrumentIndex()) return;
      seOnEngineSamplePlay(result);
   });
   EventBus.on(EVENT.patternTableChange, () => renderSongSeq());
   EventBus.on(EVENT.trackCountChange, () => {
      dom.trackCount.value = Tracker.getTrackCount();
      renderPattern();
      // Re-render MIDI routing if MIDI panel is visible
      if (!$('xt-bpanel-midi').hidden) {
         midiPanel.renderRouting();
         initNumberSteppers(document.getElementById('xt-bpanel-midi'));
      }
   });
   EventBus.on(EVENT.songPropertyChange, () => {
      renderInstruments();
      renderSampleSlots();
      renderSampleEditor();
      renderSongSeq();
      updateTransport();
      renderPattern();
   });
   EventBus.on(EVENT.songLoaded, () => {
      renderInstruments();
      renderSampleSlots();
      renderSampleEditor();
      renderSongSeq();
      updateTransport();
      cursor = { track: 0, row: 0, col: 0 };
      renderPattern();
   });
   EventBus.on(EVENT.trackerModeChanged, () => {
      dom.formatSelect.value = Tracker.inFTMode() ? 'xm' : 'mod';
      renderPattern();
   });
}

// ============================================================================
// TRANSPORT WIRING
// ============================================================================
function setEditMode(edit) {
   editMode = edit;
   dom.modeEditBtn.classList.toggle('active', edit);
   document.body.classList.toggle('xt-edit-mode', edit);
   document.body.classList.toggle('xt-play-mode', !edit);
   renderPattern();
}

function wireTransport() {
   dom.playBtn.addEventListener('click', () => {
      ensureAudio();
      if (Tracker.isPlaying()) {
         Tracker.stop();
      } else {
         const mode = dom.playmodeSelect.value;
         if (mode === 'pattern') Tracker.playPattern();
         else Tracker.playSong();
      }
   });
   if (dom.recordBtn) {
      dom.recordBtn.addEventListener('click', () => {
         recordMode = !recordMode;
         dom.recordBtn.classList.toggle('active', recordMode);
      });
   }
   // Edit mode toggle
   if (dom.modeEditBtn) {
      dom.modeEditBtn.addEventListener('click', () => setEditMode(!editMode));
   }
   dom.bpmInput.addEventListener('change', () => Tracker.setBPM(parseInt(dom.bpmInput.value, 10) || 125));
   dom.speedInput.addEventListener('change', () => Tracker.setAmigaSpeed(parseInt(dom.speedInput.value, 10) || 6));
   dom.playmodeSelect.addEventListener('change', () => {
      Tracker.setPlayType(dom.playmodeSelect.value === 'pattern' ? 2 : 1);
   });
   dom.newBtn.addEventListener('click', () => {
      Tracker.new();
      cursor = { track: 0, row: 0, col: 0 };
      renderAll();
   });
   dom.loadBtn.addEventListener('click', () => dom.fileInput.click());
   dom.fileInput.addEventListener('change', (e) => {
      const files = e.target.files;
      if (files && files.length) Tracker.handleUpload(files);
      e.target.value = '';
   });
   dom.saveBtn.addEventListener('click', () => saveModule());
   dom.exportBtn.addEventListener('click', () => exportWav());
}

// ============================================================================
// PATTERN EDITOR WIRING (click + keyboard)
// ============================================================================
function setCursor(track, row, col) {
   const tracks = Tracker.getTrackCount();
   const len = Tracker.getPatternLength();
   cursor.track = Math.max(0, Math.min(tracks - 1, track));
   cursor.row = Math.max(0, Math.min(len - 1, row));
   if (col != null) cursor.col = Math.max(0, Math.min(4, col));
   Editor.setCurrentTrack(cursor.track);
   Editor.setCurrentCursorPosition(cursor.row);
}

// Get the "add step" value — how many rows the cursor advances after note entry
function getAddStep() {
   return Math.max(1, parseInt(dom.addInput.value, 10) || 1);
}

// Advance cursor down by the add step (with wrap-around like ggv_2)
function advanceCursor() {
   const len = Tracker.getPatternLength();
   let newRow = cursor.row + getAddStep();
   if (newRow >= len) newRow = newRow - len;
   setCursor(cursor.track, newRow, cursor.col);
}

function startSelection(track, row) {
   selection = { startTrack: track, startRow: row, endTrack: track, endRow: row };
   selectionAnchor = { track, row };
}

function extendSelection(track, row) {
   if (!selectionAnchor) { startSelection(track, row); return; }
   selection = {
      startTrack: selectionAnchor.track, startRow: selectionAnchor.row,
      endTrack: track, endRow: row,
   };
}

function clearSelection() {
   selection = null;
   selectionAnchor = null;
}

function wirePatternEditor() {
   // Mouse down on cell: edit mode = select/drag, playback mode = move cursor only
   dom.patternGrid.addEventListener('mousedown', (e) => {
      const cell = e.target.closest('.xt-cell');
      if (!cell) return;
      e.preventDefault();
      const t = parseInt(cell.dataset.track, 10);
      const r = parseInt(cell.dataset.row, 10);
      if (!editMode) {
         // Playback mode: just move cursor, no selection
         clearSelection();
         setCursor(t, r);
         renderPattern();
         return;
      }
      if (e.ctrlKey || e.metaKey) {
         // Ctrl+drag: start or extend selection
         if (selectionAnchor) {
            extendSelection(t, r);
         } else {
            startSelection(t, r);
         }
         setCursor(t, r);
         isSelecting = true;
      } else if (e.shiftKey && selectionAnchor) {
         // Shift+click: extend selection from anchor
         extendSelection(t, r);
         setCursor(t, r);
      } else {
         // Plain click: move cursor only, clear selection
         clearSelection();
         setCursor(t, r);
      }
      renderPattern();
   });

   // Mouse drag: extend selection
   dom.patternGrid.addEventListener('mousemove', (e) => {
      if (!isSelecting) return;
      const cell = e.target.closest('.xt-cell');
      if (!cell) return;
      const t = parseInt(cell.dataset.track, 10);
      const r = parseInt(cell.dataset.row, 10);
      extendSelection(t, r);
      setCursor(t, r);
      renderPattern();
   });

   document.addEventListener('mouseup', () => { isSelecting = false; });

   // Double-click: clear selection
   dom.patternGrid.addEventListener('dblclick', () => {
      if (!editMode) return;
      clearSelection();
      renderPattern();
   });

   // Right-click on cell: context menu for sub-column editing
   dom.patternGrid.addEventListener('contextmenu', (e) => {
      const cell = e.target.closest('.xt-cell');
      if (!cell) return;
      e.preventDefault();
      if (!editMode) return;
      const t = parseInt(cell.dataset.track, 10);
      const r = parseInt(cell.dataset.row, 10);
      // Determine which sub-column was clicked
      let col = 0;
      const noteEl = e.target.closest('.xt-cell-note');
      const insEl = e.target.closest('.xt-cell-ins');
      const volEl = e.target.closest('.xt-cell-vol');
      const fxEl = e.target.closest('.xt-cell-fx');
      const paramEl = e.target.closest('.xt-cell-param');
      if (insEl) col = 1;
      else if (volEl) col = 2;
      else if (fxEl) col = 3;
      else if (paramEl) col = 4;
      setCursor(t, r, col);
      renderPattern();
      showCellContextMenu(e.clientX, e.clientY, col, t, r);
   });

   dom.patternNum.addEventListener('change', () => {
      Tracker.setCurrentPattern(parseInt(dom.patternNum.value, 10) || 0);
   });
   dom.patternLen.addEventListener('change', () => {
      Tracker.setPatternLength(parseInt(dom.patternLen.value, 10) || 64);
      renderPattern();
   });
   dom.trackCount.addEventListener('change', () => {
      Tracker.setTrackCount(parseInt(dom.trackCount.value, 10) || 4);
      renderPattern();
   });
   dom.ptClear.addEventListener('click', () => { Editor.clearPattern(); renderPattern(); });
   // Whole-pattern copy/paste (toolbar buttons)
   dom.ptCopy.addEventListener('click', () => { copySelectionOrPattern(); });
   dom.ptPaste.addEventListener('click', () => { pasteAtCursor(); renderPattern(); });
}

// ============================================================================
// CELL CONTEXT MENU — right-click to set values per sub-column
// ============================================================================
let cellCtxMenu = null;

function closeCellContextMenu() {
   if (cellCtxMenu) { cellCtxMenu.remove(); cellCtxMenu = null; }
}

function cellMenuItem(label, onClick, opts = {}) {
   const el = document.createElement('div');
   el.className = 'we-ctxmenu__item';
   if (opts.disabled) el.classList.add('we-ctxmenu__item--disabled');
   if (opts.hasSubmenu) el.classList.add('we-ctxmenu__item--has-submenu');
   el.textContent = label;
   if (opts.shortcut) {
      const sc = document.createElement('span');
      sc.className = 'we-ctxmenu__shortcut';
      sc.textContent = opts.shortcut;
      el.appendChild(sc);
   }
   if (onClick) el.addEventListener('click', () => { onClick(); closeCellContextMenu(); });
   return el;
}

function cellMenuSeparator() {
   const el = document.createElement('div');
   el.className = 'we-ctxmenu__item we-ctxmenu__item--separator';
   return el;
}

function getNoteAt(track, row) {
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern || !pattern[row]) return null;
   let note = pattern[row][track];
   if (!note) {
      note = Note();
      pattern[row][track] = note;
   }
   return note;
}
// ============================================================================
// CHANNEL HEADERS — mute/solo buttons + waveform animation
// ============================================================================
let mutedChannels = new Set();
let soloChannels = new Set();
let waveAnimRAF = null;

// Apply mute/solo state to the audio engine
function applyChannelMuteState() {
   const tracks = Tracker.getTrackCount();
   const filterChains = Audio.filterChains || [];
   for (let t = 0; t < tracks; t++) {
      const fc = filterChains[t];
      if (!fc) continue;
      const audible = isChannelAudible(t);
      fc.volumeValue(audible ? 70 : 0);
   }
}

function toggleMuteChannel(ch) {
   if (mutedChannels.has(ch)) mutedChannels.delete(ch);
   else mutedChannels.add(ch);
   applyChannelMuteState();
   renderChannelHeaders();
}

function toggleSoloChannel(ch) {
   if (soloChannels.has(ch)) soloChannels.delete(ch);
   else soloChannels.add(ch);
   applyChannelMuteState();
   renderChannelHeaders();
}

function isChannelAudible(ch) {
   if (soloChannels.size > 0) return soloChannels.has(ch);
   return !mutedChannels.has(ch);
}

function renderChannelHeaders() {
   const container = $('xt-channel-headers');
   if (!container) return;
   const tracks = Tracker.getTrackCount();
   container.innerHTML = '';
   const frag = document.createDocumentFragment();
   const spacer = document.createElement('div');
   spacer.style.cssText = 'width:34px;flex-shrink:0;border-right:1px solid var(--c2);';
   frag.appendChild(spacer);
   for (let t = 0; t < tracks; t++) {
      const header = document.createElement('div');
      header.className = 'xt-ch-header';
      if (mutedChannels.has(t)) header.classList.add('xt-ch-muted');
      if (soloChannels.has(t)) header.classList.add('xt-ch-solo');
      if (t === cursor.track) header.classList.add('xt-ch-selected');

      const controls = document.createElement('div');
      controls.className = 'xt-ch-controls';

      const muteBtn = document.createElement('button');
      muteBtn.className = 'xt-ch-btn xt-ch-mute-btn';
      muteBtn.textContent = 'MUTE';
      muteBtn.title = 'Mute channel ' + (t + 1);
      if (mutedChannels.has(t)) muteBtn.classList.add('active');
      muteBtn.onclick = (e) => { e.stopPropagation(); toggleMuteChannel(t); };
      controls.appendChild(muteBtn);

      const soloBtn = document.createElement('button');
      soloBtn.className = 'xt-ch-btn xt-ch-solo-btn';
      soloBtn.textContent = 'SOLO';
      soloBtn.title = 'Solo channel ' + (t + 1);
      if (soloChannels.has(t)) soloBtn.classList.add('active');
      soloBtn.onclick = (e) => { e.stopPropagation(); toggleSoloChannel(t); };
      controls.appendChild(soloBtn);

      const label = document.createElement('span');
      label.className = 'xt-ch-label';
      label.textContent = String(t + 1).padStart(2, '0');
      controls.appendChild(label);
      header.appendChild(controls);

      // Click on header (not on M/S buttons) selects the channel
      header.dataset.track = t;
      header.addEventListener('mousedown', (e) => {
         if (e.target.closest('.xt-ch-btn')) return; // don't intercept M/S clicks
         e.preventDefault();
         setCursor(t, cursor.row, 0);
         renderPattern();
      });

      const waveDiv = document.createElement('div');
      waveDiv.className = 'xt-ch-wave';
      const canvas = document.createElement('canvas');
      canvas.className = 'xt-ch-wave-canvas';
      canvas.width = 120;
      canvas.height = 20;
      canvas.dataset.track = t;
      waveDiv.appendChild(canvas);
      header.appendChild(waveDiv);

      frag.appendChild(header);
   }
   container.appendChild(frag);
   startWaveformAnimation();
}

function startWaveformAnimation() {
   if (waveAnimRAF) cancelAnimationFrame(waveAnimRAF);
   const canvases = document.querySelectorAll('.xt-ch-wave-canvas');
   if (canvases.length === 0) return;

   const analysers = Audio.trackAnalysers || [];
   const waveBuffers = Array.from(canvases).map(c => {
      const t = parseInt(c.dataset.track, 10);
      const analyser = analysers[t];
      return analyser ? new Uint8Array(analyser.frequencyBinCount) : null;
   });

   function draw() {
      canvases.forEach((canvas, idx) => {
         const t = parseInt(canvas.dataset.track, 10);
         const ctx = canvas.getContext('2d');
         const w = canvas.width, h = canvas.height;
         ctx.clearRect(0, 0, w, h);

         const audible = isChannelAudible(t);
         const analyser = analysers[t];
         const buf = waveBuffers[idx];
         const tc = window.themeColors || [];
         const waveColor = tc[15] || '#cccccc';
         const dimColor = tc[5] || '#555555';

         if (analyser && buf) {
            // Get real time-domain waveform data from the analyser
            analyser.getByteTimeDomainData(buf);
            const mid = h / 2;
            ctx.beginPath();
            ctx.strokeStyle = audible ? waveColor : dimColor;
            ctx.lineWidth = 1;
            const step = buf.length / w;
            for (let i = 0; i < w; i++) {
               const v = (buf[Math.floor(i * step)] - 128) / 128;
               const y = mid - v * mid * 0.85;
               if (i === 0) ctx.moveTo(i, y);
               else ctx.lineTo(i, y);
            }
            ctx.stroke();
            // Fill below the line subtly
            ctx.lineTo(w, mid);
            ctx.lineTo(0, mid);
            ctx.closePath();
            ctx.fillStyle = audible ? 'rgba(204,204,204,0.10)' : 'rgba(85,85,85,0.05)';
            ctx.fill();
         } else {
            // No analyser — draw flat line
            const mid = h / 2;
            ctx.beginPath();
            ctx.strokeStyle = dimColor;
            ctx.lineWidth = 1;
            ctx.moveTo(0, mid);
            ctx.lineTo(w, mid);
            ctx.stroke();
         }
      });
      waveAnimRAF = requestAnimationFrame(draw);
   }
   draw();
}

function simulateChannelWaves() {
   // No longer needed — waveforms use real analyser data
}



// ============================================================================
// PIANO KEYBOARD POPUP (dawseur step-sequencer style, adapted for notes)
// ============================================================================
function showNotePianoKeyboard(e, track, row) {
   closeCellContextMenu();
   const existing = document.querySelector('.xt-note-piano-kb');
   if (existing) existing.remove();

   const curNote = getNoteAt(track, row);
   const currentIdx = curNote ? curNote.index : 0;
   let octave = parseInt(dom.octaveInput.value, 10) || 4;

   const popup = document.createElement('div');
   popup.className = 'xt-note-piano-kb';
   popup.style.cssText = 'position:fixed;z-index:99999;background:var(--c1);border:1px solid var(--c4);border-radius:3px;box-shadow:0 4px 12px rgba(0,0,0,0.5);font-family:bit,monospace;font-size:10px;padding:6px;display:flex;flex-direction:column;gap:4px;';

   const header = document.createElement('div');
   header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;color:var(--ce);';
   const title = document.createElement('span');
   title.textContent = 'Note  T' + (track + 1) + ' R' + row;
   header.appendChild(title);
   const closeBtn = document.createElement('span');
   closeBtn.textContent = 'x';
   closeBtn.style.cssText = 'cursor:pointer;color:var(--c6);';
   closeBtn.onclick = () => popup.remove();
   header.appendChild(closeBtn);
   popup.appendChild(header);

   const valDisplay = document.createElement('div');
   valDisplay.style.cssText = 'text-align:center;color:var(--c9);font-size:11px;padding:2px;';
   function updateValDisplay() {
      const n = getNoteAt(track, row);
      valDisplay.textContent = n && n.index ? getNoteString(n) : '---';
   }
   updateValDisplay();
   popup.appendChild(valDisplay);

   const octaveRow = document.createElement('div');
   octaveRow.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:6px;';
   const octDown = document.createElement('button');
   octDown.textContent = 'Oct-';
   octDown.className = 'xt-btn';
   octDown.style.cssText = 'padding:2px 6px;font-size:9px;height:auto;min-width:auto;';
   const octLabel = document.createElement('span');
   octLabel.style.cssText = 'color:var(--ce);min-width:40px;text-align:center;';
   octLabel.textContent = 'Oct ' + octave;
   const octUp = document.createElement('button');
   octUp.textContent = 'Oct+';
   octUp.className = 'xt-btn';
   octUp.style.cssText = 'padding:2px 6px;font-size:9px;height:auto;min-width:auto;';
   octDown.onclick = () => { octave = Math.max(0, octave - 1); octLabel.textContent = 'Oct ' + octave; drawKeyboard(); };
   octUp.onclick = () => { octave = Math.min(8, octave + 1); octLabel.textContent = 'Oct ' + octave; drawKeyboard(); };
   octaveRow.appendChild(octDown);
   octaveRow.appendChild(octLabel);
   octaveRow.appendChild(octUp);
   popup.appendChild(octaveRow);

   const kbWrap = document.createElement('div');
   kbWrap.style.cssText = 'position:relative;display:flex;gap:1px;';
   popup.appendChild(kbWrap);

   const WHITE_KEYS = [
      { key: 'Z', note: 0 }, { key: 'X', note: 2 }, { key: 'C', note: 4 },
      { key: 'V', note: 5 }, { key: 'B', note: 7 }, { key: 'N', note: 9 },
      { key: 'M', note: 11 }, { key: ',', note: 12 },
   ];
   const BLACK_KEYS = [
      { key: 'S', note: 1, afterWhite: 0 }, { key: 'D', note: 3, afterWhite: 1 },
      { key: 'G', note: 6, afterWhite: 3 }, { key: 'H', note: 8, afterWhite: 4 },
      { key: 'J', note: 10, afterWhite: 5 },
   ];
   const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

   function drawKeyboard() {
      kbWrap.innerHTML = '';
      const wW = 22, wH = 50, bW = 14, bH = 32;
      const whiteRow = document.createElement('div');
      whiteRow.style.cssText = 'display:flex;gap:1px;position:relative;';
      for (const wk of WHITE_KEYS) {
         const noteIdx = octave * 12 + wk.note + 1;
         const isActive = currentIdx === noteIdx;
         const keyEl = document.createElement('div');
         keyEl.style.cssText = `width:${wW}px;height:${wH}px;background:${isActive ? 'var(--cb)' : 'var(--ce)'};border:1px solid var(--c3);display:flex;flex-direction:column;align-items:center;justify-content:flex-end;cursor:pointer;color:var(--c0);font-size:8px;padding:1px;`;
         keyEl.innerHTML = `<span style="font-size:7px;opacity:0.7;">${wk.key}</span><span>${noteNames[wk.note % 12]}${octave + Math.floor(wk.note / 12)}</span>`;
         keyEl.onclick = () => {
            const n = getNoteAt(track, row);
            n.setIndex(noteIdx);
            n.instrument = Tracker.getCurrentInstrumentIndex();
            updateValDisplay(); drawKeyboard(); renderPattern();
            previewNote(noteIdx);
         };
         whiteRow.appendChild(keyEl);
      }
      for (const bk of BLACK_KEYS) {
         const noteIdx = octave * 12 + bk.note + 1;
         const isActive = currentIdx === noteIdx;
         const keyEl = document.createElement('div');
         keyEl.style.cssText = `position:absolute;width:${bW}px;height:${bH}px;background:${isActive ? 'var(--cb)' : 'var(--c0)'};border:1px solid var(--c3);left:${bk.afterWhite * (wW + 1) + (wW - bW / 2) - 1}px;top:0;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;cursor:pointer;color:var(--ce);font-size:7px;padding:1px;`;
         keyEl.innerHTML = `<span style="opacity:0.7;">${bk.key}</span><span>${noteNames[bk.note]}${octave}</span>`;
         keyEl.onclick = () => {
            const n = getNoteAt(track, row);
            n.setIndex(noteIdx);
            n.instrument = Tracker.getCurrentInstrumentIndex();
            updateValDisplay(); drawKeyboard(); renderPattern();
            previewNote(noteIdx);
         };
         whiteRow.appendChild(keyEl);
      }
      kbWrap.appendChild(whiteRow);
   }

   function previewNote(noteIdx) {
      if (!audioCtx) return;
      try {
         const t = audioCtx.currentTime;
         const inst = Tracker.getCurrentInstrument();
         if (inst && inst.play) {
            const n = getNoteAt(track, row);
            inst.play(noteIdx, n.period, 64, track, [], t);
         }
      } catch (_) {}
   }

   drawKeyboard();

   const btnRow = document.createElement('div');
   btnRow.style.cssText = 'display:flex;gap:4px;justify-content:center;';
   const clearBtn = document.createElement('button');
   clearBtn.textContent = 'Clear (---)';
   clearBtn.className = 'xt-btn';
   clearBtn.style.cssText = 'padding:2px 8px;font-size:9px;height:auto;min-width:auto;';
   clearBtn.onclick = () => {
      const n = getNoteAt(track, row);
      n.index = 0; n.period = 0;
      updateValDisplay(); drawKeyboard(); renderPattern();
   };
   btnRow.appendChild(clearBtn);
   const offBtn = document.createElement('button');
   offBtn.textContent = 'Note Off (===)';
   offBtn.className = 'xt-btn';
   offBtn.style.cssText = 'padding:2px 8px;font-size:9px;height:auto;min-width:auto;';
   offBtn.onclick = () => {
      const n = getNoteAt(track, row);
      n.index = NOTEOFF; n.period = NOTEOFF;
      updateValDisplay(); drawKeyboard(); renderPattern();
      popup.remove();
   };
   btnRow.appendChild(offBtn);
   popup.appendChild(btnRow);

   document.body.appendChild(popup);
   let px = e.clientX, py = e.clientY;
   const rect = popup.getBoundingClientRect();
   if (px + rect.width > window.innerWidth) px = window.innerWidth - rect.width - 4;
   if (py + rect.height > window.innerHeight) py = window.innerHeight - rect.height - 4;
   popup.style.left = px + 'px';
   popup.style.top = py + 'px';

   setTimeout(() => {
      const closeHandler = (ev) => {
         if (!popup.contains(ev.target)) {
            popup.remove();
            document.removeEventListener('mousedown', closeHandler, true);
         }
      };
      document.addEventListener('mousedown', closeHandler, true);
   }, 0);
}

function showCellContextMenu(x, y, col, track, row) {
   closeCellContextMenu();
   const menu = document.createElement('div');
   menu.className = 'we-ctxmenu';
   const scroll = document.createElement('div');
   scroll.className = 'we-ctxmenu__scroll';
   menu.appendChild(scroll);

   if (col === 0) {
      // ---- NOTE column: piano keyboard popup (dawseur-style) ----
      scroll.appendChild(cellMenuItem('--- Clear Note', () => {
         const n = getNoteAt(track, row);
         n.index = 0; n.period = 0; n.instrument = 0;
         renderPattern();
      }, { shortcut: 'Del' }));
      scroll.appendChild(cellMenuItem('=== Note Off', () => {
         const n = getNoteAt(track, row);
         n.index = NOTEOFF; n.period = NOTEOFF;
         renderPattern();
      }));
      scroll.appendChild(cellMenuSeparator());
      scroll.appendChild(cellMenuItem('Piano Keyboard...', () => {
         showNotePianoKeyboard({ clientX: x, clientY: y }, track, row);
      }));
   } else if (col === 1) {
      // ---- INSTRUMENT column: instrument picker ----
      scroll.appendChild(cellMenuItem('.. Clear', () => {
         const n = getNoteAt(track, row);
         n.instrument = 0;
         renderPattern();
      }));
      scroll.appendChild(cellMenuSeparator());
      const song = Tracker.getSong();
      const maxInst = song && song.instruments ? song.instruments.length : 32;
      for (let i = 1; i <= Math.min(maxInst, 32); i++) {
         const inst = song && song.instruments && song.instruments[i];
         const name = inst && inst.name ? inst.name : '';
         scroll.appendChild(cellMenuItem(hex2(i) + (name ? ' ' + name : ''), () => {
            const n = getNoteAt(track, row);
            n.instrument = i;
            renderPattern();
         }));
      }
   } else if (col === 2) {
      // ---- VOLUME column: volume + volume effects ----
      scroll.appendChild(cellMenuItem('.. Clear', () => {
         const n = getNoteAt(track, row);
         n.volumeEffect = 0;
         renderPattern();
      }));
      scroll.appendChild(cellMenuSeparator());
      // Volume slider (0-64, displayed as hex)
      const volSliderBox = document.createElement('div');
      volSliderBox.style.cssText = 'padding:6px 10px;display:flex;flex-direction:column;gap:4px;';
      const volRow = document.createElement('div');
      volRow.style.cssText = 'display:flex;align-items:center;gap:8px;';
      const volSlider = document.createElement('input');
      volSlider.type = 'range';
      volSlider.min = 0; volSlider.max = 64; volSlider.step = 1;
      const curNote = getNoteAt(track, row);
      volSlider.value = curNote && curNote.volumeEffect >= 16 ? curNote.volumeEffect - 16 : 0;
      volSlider.style.cssText = 'flex:1;accent-color:var(--cb);';
      const volVal = document.createElement('span');
      volVal.style.cssText = 'font-family:bit,monospace;font-size:11px;color:var(--ce);min-width:24px;text-align:center;';
      volVal.textContent = hex2(parseInt(volSlider.value));
      volSlider.addEventListener('input', () => { volVal.textContent = hex2(parseInt(volSlider.value)); });
      volSlider.addEventListener('change', () => {
         const n = getNoteAt(track, row);
         n.volumeEffect = 16 + parseInt(volSlider.value);
         renderPattern();
      });
      volRow.appendChild(volSlider);
      volRow.appendChild(volVal);
      volSliderBox.appendChild(volRow);
      scroll.appendChild(volSliderBox);
      scroll.appendChild(cellMenuSeparator());
      // Volume effects (XM)
      const volEffects = [
         { hi: 0x5, label: '- Slide down' },
         { hi: 0x6, label: '+ Slide up' },
         { hi: 0x7, label: '↓ Fade down' },
         { hi: 0x8, label: '↑ Fade up' },
         { hi: 0x9, label: 'S Sustain' },
         { hi: 0xA, label: 'V Vibrato' },
         { hi: 0xB, label: 'P Pan' },
         { hi: 0xC, label: '< Portamento' },
         { hi: 0xD, label: '> Porta up' },
         { hi: 0xE, label: 'M Tremolo' },
      ];
      volEffects.forEach(ve => {
         scroll.appendChild(cellMenuItem(ve.label, null, { hasSubmenu: true }));
      });
   } else if (col === 3) {
      // ---- EFFECT column: effect command picker ----
      scroll.appendChild(cellMenuItem('. Clear', () => {
         const n = getNoteAt(track, row);
         n.effect = 0; n.param = 0;
         renderPattern();
      }));
      scroll.appendChild(cellMenuSeparator());
      const effects = Tracker.inFTMode() ? [
         { e: 0x0, label: '0xy Arpeggio' },
         { e: 0x1, label: '1xy Porta up' },
         { e: 0x2, label: '2xy Porta down' },
         { e: 0x3, label: '3xy Tone porta' },
         { e: 0x4, label: '4xy Vibrato' },
         { e: 0x5, label: '5xy Porta+Vol' },
         { e: 0x6, label: '6xy Vibrato+Vol' },
         { e: 0x7, label: '7xy Tremolo' },
         { e: 0x8, label: '8xy Pan' },
         { e: 0x9, label: '9xy Sample offset' },
         { e: 0xA, label: 'Axy Vol slide' },
         { e: 0xB, label: 'Bxy Position jump' },
         { e: 0xC, label: 'Cxy Set volume' },
         { e: 0xD, label: 'Dxy Pattern break' },
         { e: 0xE, label: 'Exy Extended...' },
         { e: 0xF, label: 'Fxy Set speed/BPM' },
      ] : [
         { e: 0x0, label: '0xy Arpeggio' },
         { e: 0x1, label: '1xy Porta up' },
         { e: 0x2, label: '2xy Porta down' },
         { e: 0x3, label: '3xy Tone porta' },
         { e: 0x4, label: '4xy Vibrato' },
         { e: 0x5, label: '5xy Porta+Vol' },
         { e: 0x6, label: '6xy Vibrato+Vol' },
         { e: 0x7, label: '7xy Tremolo' },
         { e: 0x8, label: '8xy Pan' },
         { e: 0x9, label: '9xy Sample offset' },
         { e: 0xA, label: 'Axy Vol slide' },
         { e: 0xB, label: 'Bxy Position jump' },
         { e: 0xC, label: 'Cxy Set volume' },
         { e: 0xD, label: 'Dxy Pattern break' },
         { e: 0xE, label: 'Exy Extended' },
         { e: 0xF, label: 'Fxy Set speed/BPM' },
      ];
      effects.forEach(ef => {
         scroll.appendChild(cellMenuItem(ef.label, () => {
            const n = getNoteAt(track, row);
            n.effect = ef.e;
            renderPattern();
         }));
      });
   } else if (col === 4) {
      // ---- PARAM column: hex value picker ----
      scroll.appendChild(cellMenuItem('.. Clear', () => {
         const n = getNoteAt(track, row);
         n.param = 0;
         renderPattern();
      }));
      scroll.appendChild(cellMenuSeparator());
      // Hex value slider (00-FF)
      const paramSliderBox = document.createElement('div');
      paramSliderBox.style.cssText = 'padding:6px 10px;display:flex;flex-direction:column;gap:4px;';
      const paramRow = document.createElement('div');
      paramRow.style.cssText = 'display:flex;align-items:center;gap:8px;';
      const paramSlider = document.createElement('input');
      paramSlider.type = 'range';
      paramSlider.min = 0; paramSlider.max = 255; paramSlider.step = 1;
      const curNoteP = getNoteAt(track, row);
      paramSlider.value = curNoteP ? (curNoteP.param || 0) : 0;
      paramSlider.style.cssText = 'flex:1;accent-color:var(--cb);';
      const paramVal = document.createElement('span');
      paramVal.style.cssText = 'font-family:bit,monospace;font-size:11px;color:var(--ce);min-width:28px;text-align:center;';
      paramVal.textContent = hex2(parseInt(paramSlider.value));
      paramSlider.addEventListener('input', () => { paramVal.textContent = hex2(parseInt(paramSlider.value)); });
      paramSlider.addEventListener('change', () => {
         const n = getNoteAt(track, row);
         n.param = parseInt(paramSlider.value);
         renderPattern();
      });
      paramRow.appendChild(paramSlider);
      paramRow.appendChild(paramVal);
      paramSliderBox.appendChild(paramRow);
      scroll.appendChild(paramSliderBox);
   }

   // Position and show
   document.body.appendChild(menu);
   cellCtxMenu = menu;
   const rect = menu.getBoundingClientRect();
   let left = x, top = y;
   if (left + rect.width > window.innerWidth) left = Math.max(0, window.innerWidth - rect.width - 4);
   if (top + rect.height > window.innerHeight) top = Math.max(0, window.innerHeight - rect.height - 4);
   menu.style.left = `${left}px`;
   menu.style.top = `${top}px`;
}

// Close cell context menu on any click outside
document.addEventListener('mousedown', (e) => {
   if (cellCtxMenu && !cellCtxMenu.contains(e.target)) closeCellContextMenu();
});

// ============================================================================
// SELECTION CLIPBOARD (copy/cut/paste/duplicate)
// ============================================================================
function getSelectedNotes() {
   if (!selection) return [];
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return [];
   const t0 = Math.min(selection.startTrack, selection.endTrack);
   const t1 = Math.max(selection.startTrack, selection.endTrack);
   const r0 = Math.min(selection.startRow, selection.endRow);
   const r1 = Math.max(selection.startRow, selection.endRow);
   const result = [];
   for (let r = r0; r <= r1; r++) {
      for (let t = t0; t <= t1; t++) {
         const note = pattern[r] && pattern[r][t];
         result.push({ track: t - t0, row: r - r0, note: cloneNote(note) });
      }
   }
   return result;
}

function cloneNote(note) {
   if (!note) return null;
   const n = Note();
   if (note.index) { n.index = note.index; n.period = note.period; }
   if (note.instrument) n.instrument = note.instrument;
   if (note.volumeEffect) n.volumeEffect = note.volumeEffect;
   if (note.effect) n.effect = note.effect;
   if (note.param != null) n.param = note.param;
   return n;
}

function copySelectionOrPattern() {
   if (selection) {
      patternClipboard = getSelectedNotes();
   } else {
      // Copy entire pattern
      const pattern = Tracker.getCurrentPatternData();
      const tracks = Tracker.getTrackCount();
      const len = Tracker.getPatternLength();
      patternClipboard = [];
      for (let r = 0; r < len; r++) {
         for (let t = 0; t < tracks; t++) {
            const note = pattern[r] && pattern[r][t];
            patternClipboard.push({ track: t, row: r, note: cloneNote(note) });
         }
      }
   }
}

function cutSelection() {
   if (!selection) return;
   copySelectionOrPattern();
   deleteSelection();
}

function deleteSelection() {
   if (!selection) return;
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const t0 = Math.min(selection.startTrack, selection.endTrack);
   const t1 = Math.max(selection.startTrack, selection.endTrack);
   const r0 = Math.min(selection.startRow, selection.endRow);
   const r1 = Math.max(selection.startRow, selection.endRow);
   for (let r = r0; r <= r1; r++) {
      for (let t = t0; t <= t1; t++) {
         const note = pattern[r] && pattern[r][t];
         if (note) note.clear();
      }
   }
   renderPattern();
}

function pasteAtCursor() {
   if (!patternClipboard || patternClipboard.length === 0) return;
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const len = Tracker.getPatternLength();
   const tracks = Tracker.getTrackCount();
   for (const clip of patternClipboard) {
      const t = cursor.track + clip.track;
      const r = cursor.row + clip.row;
      if (t < 0 || t >= tracks || r < 0 || r >= len) continue;
      if (!pattern[r]) pattern[r] = [];
      if (clip.note) {
         pattern[r][t] = cloneNote(clip.note);
      } else {
         pattern[r][t] = Note();
      }
   }
}

function interpolateSelection() {
   if (!selection) return;
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const t0 = Math.min(selection.startTrack, selection.endTrack);
   const t1 = Math.max(selection.startTrack, selection.endTrack);
   const r0 = Math.min(selection.startRow, selection.endRow);
   const r1 = Math.max(selection.startRow, selection.endRow);
   if (r1 - r0 < 2) return; // need at least 3 rows to interpolate

   for (let t = t0; t <= t1; t++) {
      // Find first and last note with an index in the selection range
      let firstNote = null, firstRow = -1, lastNote = null, lastRow = -1;
      for (let r = r0; r <= r1; r++) {
         const n = pattern[r] && pattern[r][t];
         if (n && n.index && n.index !== NOTEOFF) {
            if (firstRow < 0) { firstNote = n; firstRow = r; }
            lastNote = n; lastRow = r;
         }
      }
      if (firstRow < 0 || lastRow < 0 || firstRow === lastRow) continue;
      // Linearly interpolate note indices between first and last
      const startIdx = firstNote.index;
      const endIdx = lastNote.index;
      const span = lastRow - firstRow;
      for (let r = firstRow + 1; r < lastRow; r++) {
         const frac = (r - firstRow) / span;
         const interpIdx = Math.round(startIdx + (endIdx - startIdx) * frac);
         let n = pattern[r] && pattern[r][t];
         if (!n) {
            n = Note();
            if (!pattern[r]) pattern[r] = [];
            pattern[r][t] = n;
         }
         n.setIndex(interpIdx);
         n.instrument = firstNote.instrument || 0;
      }
   }
   renderPattern();
}

function duplicateSelection() {
   if (!selection) return;
   copySelectionOrPattern();
   const r1 = Math.max(selection.startRow, selection.endRow);
   const height = r1 - Math.min(selection.startRow, selection.endRow) + 1;
   cursor.row = r1 + 1;
   pasteAtCursor();
   // Move selection to pasted area
   const t0 = Math.min(selection.startTrack, selection.endTrack);
   selection = { startTrack: t0, startRow: r1 + 1, endTrack: cursor.track, endRow: cursor.row + height - 1 };
   renderPattern();
}

// ============================================================================
// KEYBOARD NOTE ENTRY (tracker-style)
// ============================================================================
// Sub-column names for navigation
// col: 0=note, 1=ins, 2=vol, 3=fx, 4=param
const NUM_COLS = 5;

function setHexBuffer(val) {
   hexBuffer = val;
   if (hexBufferTimer) clearTimeout(hexBufferTimer);
   hexBufferTimer = setTimeout(() => { hexBuffer = ''; hexBufferTimer = null; }, 800);
}

function applyHexToCursor(digit) {
   // digit is 0-15 (hex nibble)
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   let note = pattern[cursor.row] && pattern[cursor.row][cursor.track];
   if (!note) {
      note = Note();
      if (!pattern[cursor.row]) pattern[cursor.row] = [];
      pattern[cursor.row][cursor.track] = note;
   }
   const col = cursor.col;
   if (col === 1) {
      // Instrument: 2 hex digits (01-1F)
      const cur = note.instrument || 0;
      if (hexBuffer.length === 0) {
         note.instrument = (digit << 4);
         setHexBuffer(digit.toString(16));
      } else {
         note.instrument = (parseInt(hexBuffer, 16) << 4) | digit;
         hexBuffer = '';
      }
   } else if (col === 2) {
      // Volume: 2 hex digits (00-40 = volume 0-64)
      const cur = note.volumeEffect || 0;
      if (hexBuffer.length === 0) {
         note.volumeEffect = 16 + (digit << 4);
         setHexBuffer(digit.toString(16));
      } else {
         note.volumeEffect = 16 + (parseInt(hexBuffer, 16) << 4) | digit;
         if (note.volumeEffect - 16 > 64) note.volumeEffect = 16 + 64;
         hexBuffer = '';
      }
   } else if (col === 3) {
      // Effect: 1 hex digit (0-F)
      note.effect = digit;
      hexBuffer = '';
   } else if (col === 4) {
      // Effect param: 2 hex digits
      if (hexBuffer.length === 0) {
         note.param = (digit << 4);
         setHexBuffer(digit.toString(16));
      } else {
         note.param = (parseInt(hexBuffer, 16) << 4) | digit;
         hexBuffer = '';
      }
   }
   renderPattern();
}

function wireKeyboard() {
   document.addEventListener('keydown', (e) => {
      // Ignore when typing in inputs
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;

      // Space = play/stop
      if (e.code === 'Space') {
         e.preventDefault();
         ensureAudio();
         Tracker.togglePlay();
         return;
      }

      // ====================================================================
      // F-KEYS: F1-F8 = octave select, F9-F12 = pattern position jump
      // ====================================================================
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
         const fKey = parseInt(e.code.replace('F', ''), 10);
         if (fKey >= 1 && fKey <= 8) {
            e.preventDefault();
            dom.octaveInput.value = fKey;
            return;
         }
         if (fKey >= 9 && fKey <= 12) {
            e.preventDefault();
            const len = Tracker.getPatternLength();
            if (fKey === 9) setCursor(cursor.track, 0, cursor.col);
            else if (fKey === 10) setCursor(cursor.track, Math.floor(len / 4), cursor.col);
            else if (fKey === 11) setCursor(cursor.track, Math.floor(len / 2), cursor.col);
            else if (fKey === 12) setCursor(cursor.track, Math.floor(len * 3 / 4), cursor.col);
            renderPattern();
            return;
         }
      }

      // ====================================================================
      // Transpose: Ctrl+Shift+F1/F2=pattern, Ctrl+F1/F2=track, Alt+F1/F2=selection
      // ====================================================================
      if (e.code.startsWith('F')) {
         const fKey = parseInt(e.code.replace('F', ''), 10);
         if (e.ctrlKey && e.shiftKey && fKey === 1) { e.preventDefault(); transposeNotes(-1, 'pattern'); return; }
         if (e.ctrlKey && e.shiftKey && fKey === 2) { e.preventDefault(); transposeNotes(1, 'pattern'); return; }
         if (e.ctrlKey && !e.shiftKey && fKey === 1) { e.preventDefault(); transposeNotes(-1, 'track'); return; }
         if (e.ctrlKey && !e.shiftKey && fKey === 2) { e.preventDefault(); transposeNotes(1, 'track'); return; }
         if (e.altKey && fKey === 1) { e.preventDefault(); transposeNotes(-1, 'selection'); return; }
         if (e.altKey && fKey === 2) { e.preventDefault(); transposeNotes(1, 'selection'); return; }
      }

      // ====================================================================
      // Ctrl+key shortcuts (undo/redo work in any mode; rest need edit mode)
      // ====================================================================
      if (e.ctrlKey || e.metaKey) {
         if (e.code === 'KeyZ') { e.preventDefault(); doUndo(); return; }
         if (e.code === 'KeyY') { e.preventDefault(); doRedo(); return; }
         if (!editMode) return;
         if (e.code === 'KeyC') { e.preventDefault(); copySelectionOrPattern(); return; }
         if (e.code === 'KeyX') { e.preventDefault(); cutSelection(); return; }
         if (e.code === 'KeyV') {
            e.preventDefault();
            if (e.shiftKey) porousPasteAtCursor();
            else { pasteAtCursor(); renderPattern(); }
            return;
         }
         if (e.code === 'KeyA') {
            e.preventDefault();
            const tracks = Tracker.getTrackCount();
            const len = Tracker.getPatternLength();
            selection = { startTrack: 0, startRow: 0, endTrack: tracks - 1, endRow: len - 1 };
            selectionAnchor = { track: 0, row: 0 };
            renderPattern();
            return;
         }
         if (e.code === 'KeyI') { e.preventDefault(); interpolateSelection(); return; }
         if (e.code === 'KeyR') { e.preventDefault(); pasteAtCursor(); renderPattern(); return; }
         // Ctrl+Left/Right = prev/next channel (tab-to-note: jumps to note col)
         if (e.code === 'ArrowLeft') {
            e.preventDefault();
            if (cursor.col > 0) { cursor.col = 0; renderPattern(); return; }
            clearSelection();
            setCursor(cursor.track - 1, cursor.row, 0);
            renderPattern();
            return;
         }
         if (e.code === 'ArrowRight') {
            e.preventDefault();
            if (cursor.col > 0) { cursor.col = 0; renderPattern(); return; }
            clearSelection();
            setCursor(cursor.track + 1, cursor.row, 0);
            renderPattern();
            return;
         }
         // Ctrl+Delete = delete volume + effect
         if (e.code === 'Delete') {
            e.preventDefault();
            if (selection) { deleteSelection(); clearSelection(); }
            else deleteVolumeAndEffect();
            return;
         }
         // Ctrl+Shift+Left/Right = select column
         if (e.shiftKey && (e.code === 'ArrowLeft' || e.code === 'ArrowRight')) {
            e.preventDefault();
            selectColumn();
            return;
         }
         return;
      }

      // ====================================================================
      // Alt+key shortcuts
      // ====================================================================
      if (e.altKey && !e.ctrlKey) {
         if (e.code === 'Delete') {
            if (!editMode) return;
            e.preventDefault();
            deleteEffectOnly();
            return;
         }
         if (e.code === 'Backspace') {
            if (!editMode) return;
            e.preventDefault();
            insertNoteSlot();
            return;
         }
         if (e.code === 'Tab') {
            e.preventDefault();
            clearSelection();
            setCursor(cursor.track - 1, cursor.row, 0);
            renderPattern();
            return;
         }
         return;
      }

      // ====================================================================
      // Navigation: Arrow keys (with Shift for selection)
      // ====================================================================
      const withShift = e.shiftKey;
      if (e.code === 'ArrowDown') {
         e.preventDefault();
         if (withShift && editMode) { extendSelection(cursor.track, cursor.row + 1); }
         else { clearSelection(); }
         setCursor(cursor.track, cursor.row + 1);
         renderPattern();
         return;
      }
      if (e.code === 'ArrowUp') {
         e.preventDefault();
         if (withShift && editMode) { extendSelection(cursor.track, cursor.row - 1); }
         else { clearSelection(); }
         setCursor(cursor.track, cursor.row - 1);
         renderPattern();
         return;
      }
      if (e.code === 'ArrowLeft') {
         e.preventDefault();
         if (cursor.col > 0) {
            cursor.col--;
         } else {
            if (withShift) { extendSelection(cursor.track - 1, cursor.row); }
            else { clearSelection(); }
            setCursor(cursor.track - 1, cursor.row, NUM_COLS - 1);
         }
         renderPattern();
         return;
      }
      if (e.code === 'ArrowRight') {
         e.preventDefault();
         if (cursor.col < NUM_COLS - 1) {
            cursor.col++;
         } else {
            if (withShift) { extendSelection(cursor.track + 1, cursor.row); }
            else { clearSelection(); }
            setCursor(cursor.track + 1, cursor.row, 0);
         }
         renderPattern();
         return;
      }

      // PageUp/PageDown = jump 16 rows
      if (e.code === 'PageUp') {
         e.preventDefault();
         clearSelection();
         setCursor(cursor.track, cursor.row - 16);
         renderPattern();
         return;
      }
      if (e.code === 'PageDown') {
         e.preventDefault();
         clearSelection();
         setCursor(cursor.track, cursor.row + 16);
         renderPattern();
         return;
      }

      // Home/End = first/last row
      if (e.code === 'Home') {
         e.preventDefault();
         clearSelection();
         setCursor(cursor.track, 0, cursor.col);
         renderPattern();
         return;
      }
      if (e.code === 'End') {
         e.preventDefault();
         clearSelection();
         setCursor(cursor.track, Tracker.getPatternLength() - 1, cursor.col);
         renderPattern();
         return;
      }

      // Tab = next channel, Shift+Tab = prev channel (tab-to-note: jumps to note col)
      if (e.code === 'Tab') {
         e.preventDefault();
         clearSelection();
         setCursor(cursor.track + (e.shiftKey ? -1 : 1), cursor.row, 0);
         renderPattern();
         return;
      }

      // Enter = advance to next row, col 0
      if (e.code === 'Enter') {
         e.preventDefault();
         clearSelection();
         setCursor(cursor.track, cursor.row + 1, 0);
         renderPattern();
         return;
      }

      // ====================================================================
      // Insert/Delete/Backspace (edit mode)
      // ====================================================================
      if (e.code === 'Insert') {
         if (!editMode) return;
         e.preventDefault();
         if (e.shiftKey) insertLine();
         else insertNoteSlot();
         return;
      }

      if (e.code === 'Backspace') {
         if (!editMode) return;
         e.preventDefault();
         if (e.shiftKey) deleteLine();
         else deleteNoteSlot();
         return;
      }

      if (e.code === 'Delete') {
         if (!editMode) return;
         e.preventDefault();
         if (selection) { deleteSelection(); clearSelection(); }
         else if (e.shiftKey) deleteSlotEntire();
         else { clearNoteAtCursor(); advanceCursor(); }
         renderPattern();
         return;
      }

      if (e.code === 'Escape') {
         e.preventDefault();
         clearSelection();
         renderPattern();
         return;
      }

      // ====================================================================
      // Note-off keys: Backquote, Backslash, Digit1 (like MilkyTracker)
      // ====================================================================
      if (editMode && (e.code === 'Backquote' || e.code === 'Backslash' || e.code === 'Digit1')) {
         e.preventDefault();
         clearSelection();
         cursor.col = 0;
         putNoteAtCursor(NOTEOFF, 0);
         advanceCursor();
         renderPattern();
         return;
      }

      // ====================================================================
      // Note entry via keyboard table (edit mode) — checked FIRST so note
      // keys always work regardless of which column the cursor is in
      // ====================================================================
      if (editMode) {
         const keyTable = KEYBOARDTABLE.main;
         const keyVal = keyTable[e.code];
         if (keyVal != null) {
            e.preventDefault();
            clearSelection();
            cursor.col = 0; // notes go in note column
            if (keyVal === KEYBOARDKEYS.OFF) {
               putNoteAtCursor(NOTEOFF, 0);
            } else {
               const noteIndex = keyToNoteIndex(keyVal);
               putNoteAtCursor(noteIndex);
            }
            advanceCursor();
            renderPattern();
            return;
         }
      }

      // ====================================================================
      // Hex digit entry for ins/vol/fx/param columns (edit mode)
      // ====================================================================
      if (editMode && cursor.col >= 1 && cursor.col <= 4) {
         let digit = -1;
         if (e.code >= 'Digit0' && e.code <= 'Digit9') digit = parseInt(e.code.slice(5), 10);
         else if (e.code >= 'KeyA' && e.code <= 'KeyF') digit = 10 + (e.code.charCodeAt(3) - 65);
         if (digit >= 0) {
            e.preventDefault();
            applyHexToCursor(digit);
            // Move to next row after completing 2-digit entry (for ins/vol/param)
            if (cursor.col === 3 || hexBuffer === '') {
               advanceCursor();
               renderPattern();
            }
            return;
         }
      }

      // ====================================================================
      // Volume column effect letters (XM-style, like MilkyTracker)
      // col 2: -,+,d,u,s,v,p,l,r,m for volume effects
      // ====================================================================
      if (editMode && cursor.col === 2) {
         const volEffects = {
            'Minus': 0x50,      // - = volslide down
            'Equal': 0x60,      // + = volslide up
            'KeyD': 0x70,       // d = fine volslide down
            'KeyU': 0x80,       // u = fine volslide up
            'KeyS': 0x90,       // s = vibrato rate
            'KeyV': 0xA0,       // v = vibrato depth
            'KeyP': 0xB0,       // p = set panning
            'KeyL': 0xC0,       // l = panning slide left
            'KeyR': 0xD0,       // r = panning slide right
            'KeyM': 0xE0,       // m = portamento
         };
         if (volEffects[e.code]) {
            e.preventDefault();
            const pattern = Tracker.getCurrentPatternData();
            if (pattern) {
               let note = pattern[cursor.row] && pattern[cursor.row][cursor.track];
               if (!note) {
                  note = Note();
                  if (!pattern[cursor.row]) pattern[cursor.row] = [];
                  pattern[cursor.row][cursor.track] = note;
               }
               note.volumeEffect = volEffects[e.code];
               renderPattern();
            }
            return;
         }
      }

      // ====================================================================
      // Instrument increment/decrement: +/- in instrument column
      // ====================================================================
      if (editMode && cursor.col === 1) {
         if (e.code === 'Equal' || e.code === 'NumpadAdd') {
            e.preventDefault();
            const pattern = Tracker.getCurrentPatternData();
            if (pattern) {
               let note = pattern[cursor.row] && pattern[cursor.row][cursor.track];
               if (!note) {
                  note = Note();
                  if (!pattern[cursor.row]) pattern[cursor.row] = [];
                  pattern[cursor.row][cursor.track] = note;
               }
               note.instrument = Math.min(255, (note.instrument || 0) + 1);
               renderPattern();
            }
            return;
         }
         if (e.code === 'Minus' || e.code === 'NumpadSubtract') {
            e.preventDefault();
            const pattern = Tracker.getCurrentPatternData();
            if (pattern) {
               let note = pattern[cursor.row] && pattern[cursor.row][cursor.track];
               if (!note) {
                  note = Note();
                  if (!pattern[cursor.row]) pattern[cursor.row] = [];
                  pattern[cursor.row][cursor.track] = note;
               }
               note.instrument = Math.max(0, (note.instrument || 0) - 1);
               renderPattern();
            }
            return;
         }
      }

      // ====================================================================
      // Octave +/- via numpad
      // ====================================================================
      if (e.code === 'NumpadAdd' || e.code === 'Equal') {
         const o = parseInt(dom.octaveInput.value, 10) || 4;
         if (o < 8) dom.octaveInput.value = o + 1;
         return;
      }
      if (e.code === 'NumpadSubtract' || e.code === 'Minus') {
         const o = parseInt(dom.octaveInput.value, 10) || 4;
         if (o > 0) dom.octaveInput.value = o - 1;
         return;
      }

      // ====================================================================
      // M key = mute channel (Shift+M or Ctrl+M)
      // ====================================================================
      if ((e.shiftKey || e.ctrlKey) && e.code === 'KeyM') {
         e.preventDefault();
         toggleMuteChannel(cursor.track);
         return;
      }
   });
}

function putNoteAtCursor(noteIndex, volume) {
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const inst = Tracker.getCurrentInstrumentIndex();
   let note = pattern[cursor.row] && pattern[cursor.row][cursor.track];
   if (!note) {
      note = Note();
      if (!pattern[cursor.row]) pattern[cursor.row] = [];
      pattern[cursor.row][cursor.track] = note;
   }
   if (noteIndex === NOTEOFF) {
      note.index = NOTEOFF;
      note.period = NOTEOFF;
   } else {
      note.setIndex(noteIndex);
   }
   note.instrument = inst;
   if (volume != null) note.volumeEffect = volume;
   // Play the note immediately for audible feedback
   if (audioCtx && noteIndex !== NOTEOFF) {
      try {
         const t = audioCtx.currentTime;
         const instObj = Tracker.getCurrentInstrument();
         if (instObj && instObj.play) instObj.play(noteIndex, note.period, 64, cursor.track, [], t);
      } catch (_) {}
   }
   renderPattern();
   // Advance cursor when in record mode or edit mode
   if (recordMode || editMode) advanceCursor();
}

function clearNoteAtCursor() {
   const pattern = Tracker.getCurrentPatternData();
   if (!pattern) return;
   const note = pattern[cursor.row] && pattern[cursor.row][cursor.track];
   if (note) note.clear();
   renderPattern();
}

// ============================================================================
// SAMPLE EDITOR WIRING
// ============================================================================
function wireSampleEditor() {
   const syncProp = (el, prop, isInt) => () => {
      const s = Tracker.getCurrentInstrument() && Tracker.getCurrentInstrument().sample;
      if (!s) return;
      s[prop] = isInt ? parseInt(el.value, 10) || 0 : el.value;
   };
   // Drag & drop samples from bucket onto waveform canvas
   if (dom.seWave) {
      dom.seWave.addEventListener('dragover', (e) => {
         e.preventDefault();
         if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
         dom.seWave.classList.add('dragover');
      });
      dom.seWave.addEventListener('dragleave', () => dom.seWave.classList.remove('dragover'));
      dom.seWave.addEventListener('drop', (e) => {
         e.preventDefault();
         e.stopPropagation();
         dom.seWave.classList.remove('dragover');
         const slotIndex = Tracker.getCurrentInstrumentIndex();
         handleSampleDrop(e, slotIndex);
      });
   }
   dom.seName.addEventListener('change', syncProp(dom.seName, 'name', false));
   dom.seVol.addEventListener('change', syncProp(dom.seVol, 'volume', true));
   dom.seFinetune.addEventListener('change', syncProp(dom.seFinetune, 'finetune', true));
   dom.sePan.addEventListener('change', syncProp(dom.sePan, 'panning', true));
   dom.seRelnote.addEventListener('change', syncProp(dom.seRelnote, 'relativeNote', true));
   dom.seC2spd.addEventListener('change', syncProp(dom.seC2spd, 'c2spd', true));
   dom.seLoop.addEventListener('change', () => {
      const s = Tracker.getCurrentInstrument() && Tracker.getCurrentInstrument().sample;
      if (!s) return;
      if (!s.loop) s.loop = { enabled: false, start: 0, length: 0, type: 0 };
      s.loop.enabled = dom.seLoop.checked;
   });
   dom.seLoopstart.addEventListener('change', () => {
      const s = Tracker.getCurrentInstrument() && Tracker.getCurrentInstrument().sample;
      if (!s || !s.loop) s.loop = { enabled: false, start: 0, length: 0, type: 0 };
      s.loop.start = parseInt(dom.seLoopstart.value, 10) || 0;
   });
   dom.seLooplen.addEventListener('change', () => {
      const s = Tracker.getCurrentInstrument() && Tracker.getCurrentInstrument().sample;
      if (!s || !s.loop) s.loop = { enabled: false, start: 0, length: 0, type: 0 };
      s.loop.length = parseInt(dom.seLooplen.value, 10) || 0;
   });
   dom.clearSample.addEventListener('click', () => {
      const inst = Tracker.getCurrentInstrument();
      if (!inst) return;
      const s = inst.samples && inst.samples[currentSampleSlot];
      if (s) { s.data = []; s.length = 0; s.name = ''; }
      renderSampleSlots();
      renderSampleEditor();
   });
}

// ============================================================================
// LEFT PANEL WIRING
// ============================================================================
function wireLeftPanel() {
   dom.addInstr.addEventListener('click', () => {
      const instruments = Tracker.getInstruments();
      const i = instruments.length;
      const inst = Instrument();
      inst.instrumentIndex = i;
      instruments[i] = inst;
      Tracker.setCurrentInstrumentIndex(i);
      renderInstruments();
      renderSampleSlots();
      renderSampleEditor();
   });
   dom.addPos.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const song = Tracker.getSong();
      if (!song) return;
      const last = song.patternTable[song.patternTable.length - 1] || 0;
      song.patternTable.push(last);
      song.length = song.patternTable.length;
      renderSongSeq();
   });
}

// ============================================================================
// FILE LOADING (from bucket drag-drop onto pattern area, or file input)
// ============================================================================
function wireFileDrop() {
   const dropZone = document.querySelector('.xt-pattern-area');
   if (!dropZone) return;
   ['dragover', 'dragenter'].forEach(evt => {
      dropZone.addEventListener(evt, (e) => {
         e.preventDefault();
         if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      });
   });
   dropZone.addEventListener('drop', async (e) => {
      // Only handle module drops (not sample drops which target slots)
      const bucketItemId = e.dataTransfer.getData('application/x-bucket-item-id');
      if (bucketItemId && bucket) {
         const item = bucket.items.find(it => it.id === bucketItemId);
         if (item) {
            e.preventDefault();
            let ab;
            if (item.wav instanceof ArrayBuffer) ab = item.wav;
            else if (item.wav instanceof Uint8Array) ab = item.wav.buffer;
            else if (item.data instanceof ArrayBuffer) ab = item.data;
            else if (item.data instanceof Uint8Array) ab = item.data.buffer;
            else if (item.wavLike instanceof ArrayBuffer) ab = item.wavLike;
            else if (item.wavLike instanceof Uint8Array) ab = item.wavLike.buffer;
            else return;
            loadModuleFromArrayBuffer(ab.slice(0), item.name || 'song.mod');
            return;
         }
      }
      if (e.dataTransfer.files && e.dataTransfer.files.length) {
         e.preventDefault();
         const f = e.dataTransfer.files[0];
         const ab = await f.arrayBuffer();
         loadModuleFromArrayBuffer(ab, f.name);
      }
   });
}

function loadModuleFromArrayBuffer(arrayBuffer, name) {
   ensureAudio();
   Tracker.processFile(arrayBuffer, name);
}

// ============================================================================
// MODULE/SAMPLE SEARCH (remote file browser — list & load from /media/list)
// ============================================================================
let msAbortController = null;
let msActiveTab = 'modules'; // 'modules' or 'samples'
let msSampleFolder = null;   // current folder when in samples tab

function formatFileSize(bytes) {
   if (!bytes && bytes !== 0) return '';
   if (bytes < 1024) return bytes + ' B';
   if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
   return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

async function fetchModuleList() {
   if (!dom.msResults) return;
   if (msAbortController) { try { msAbortController.abort(); } catch (_) {} }
   msAbortController = new AbortController();
   const q = (dom.msSearch && dom.msSearch.value) || '';

   if (msActiveTab === 'samples') {
      return fetchSampleList(q);
   }

   const base = '/media/list?format=module';
   const url = q ? base + '&search=' + encodeURIComponent(q) : base;
   dom.msResults.innerHTML = '<div class="xt-ms-status">loading…</div>';
   let data = [];
   let fetchError = null;
   try {
      const resp = await fetch(url, { signal: msAbortController.signal });
      if (resp.ok) data = await resp.json();
      else fetchError = 'HTTP ' + resp.status;
   } catch (err) {
      if (err.name === 'AbortError') return;
      fetchError = err && err.message ? err.message : String(err);
   }
   if (!Array.isArray(data)) data = [];
   if (fetchError) {
      dom.msResults.innerHTML = '<div class="xt-ms-status">error: ' + fetchError + '</div>';
      return;
   }
   sortMsData(data);
   dom.msResults.innerHTML = '';
   if (!data.length) {
      dom.msResults.innerHTML = '<div class="xt-ms-status">no modules found</div>';
      return;
   }
   const frag = document.createDocumentFragment();
   for (const item of data) {
      frag.appendChild(createModuleItem(item));
   }
   dom.msResults.appendChild(frag);
}

function sortMsData(data) {
   const sortValue = (dom.msSortby && dom.msSortby.value) || 'name-asc';
   data.sort((a, b) => {
      const an = (a.filename || a.name || '').toLowerCase();
      const bn = (b.filename || b.name || '').toLowerCase();
      switch (sortValue) {
         case 'name-asc': return an.localeCompare(bn);
         case 'name-desc': return bn.localeCompare(an);
         case 'size-asc': return (a.size || 0) - (b.size || 0);
         case 'size-desc': return (b.size || 0) - (a.size || 0);
         default: return 0;
      }
   });
}

function createModuleItem(item) {
   const filename = item.filename || item.name || 'unknown';
   const size = item.size || 0;
   const div = document.createElement('div');
   div.className = 'xt-ms-item';
   div.innerHTML = '<span class="xt-ms-name" title="' + filename + '">' + filename + '</span><span class="xt-ms-size">' + formatFileSize(size) + '</span>';
   div.addEventListener('click', () => {
      document.querySelectorAll('.xt-ms-item').forEach(el => el.classList.remove('active'));
      div.classList.add('active');
      loadModuleFromServer(item);
   });
   return div;
}

// --- Sample bank browsing (SAMPLES tab) ---
async function fetchSampleList(q) {
   dom.msResults.innerHTML = '<div class="xt-ms-status">loading…</div>';
   let data = [];
   let fetchError = null;
   let isFolderList = false;
   try {
      let url;
      if (q) {
         // Global search across all samples
         url = '/media/list?format=samplebank&search=' + encodeURIComponent(q);
      } else if (msSampleFolder) {
         url = '/media/list?format=samplebank&folder=' + encodeURIComponent(msSampleFolder);
      } else {
         url = '/media/list?format=samplebank';
         isFolderList = true;
      }
      const resp = await fetch(url, { signal: msAbortController.signal });
      if (resp.ok) data = await resp.json();
      else fetchError = 'HTTP ' + resp.status;
   } catch (err) {
      if (err.name === 'AbortError') return;
      fetchError = err && err.message ? err.message : String(err);
   }
   if (!Array.isArray(data)) data = [];
   if (fetchError) {
      dom.msResults.innerHTML = '<div class="xt-ms-status">error: ' + fetchError + '</div>';
      return;
   }
   dom.msResults.innerHTML = '';
   const frag = document.createDocumentFragment();

   // Back button when inside a folder
   if (msSampleFolder && !q) {
      const back = document.createElement('div');
      back.className = 'xt-ms-back';
      back.textContent = '\u2190 back to folders';
      back.addEventListener('click', () => {
         msSampleFolder = null;
         if (dom.msSearch) dom.msSearch.value = '';
         fetchSampleList('');
      });
      frag.appendChild(back);
      const hdr = document.createElement('div');
      hdr.className = 'xt-ms-folder-header';
      hdr.textContent = msSampleFolder + ' (' + data.length + ')';
      frag.appendChild(hdr);
   }

   if (isFolderList) {
      // Render folders
      if (!data.length) {
         dom.msResults.innerHTML = '<div class="xt-ms-status">no sample folders</div>';
         return;
      }
      data.sort((a, b) => (a.folder || '').localeCompare(b.folder || ''));
      for (const f of data) {
         const name = f.folder || f.name || '';
         const div = document.createElement('div');
         div.className = 'xt-ms-item xt-ms-folder';
         div.innerHTML = '<span class="xt-ms-name">' + name + '</span><span class="xt-ms-size">' + (f.type || 'folder') + '</span>';
         div.addEventListener('click', () => {
            msSampleFolder = name;
            fetchSampleList('');
         });
         frag.appendChild(div);
      }
   } else {
      // Render sample items (draggable to bucket)
      if (!data.length) {
         dom.msResults.innerHTML = '<div class="xt-ms-status">' + (q ? 'no samples matching "' + q + '"' : 'no samples') + '</div>';
         return;
      }
      sortMsData(data);
      for (const item of data) {
         frag.appendChild(createSampleItem(item, q));
      }
   }
   dom.msResults.appendChild(frag);
}

function createSampleItem(item, searchQuery) {
   const filename = item.filename || item.name || 'unknown';
   const size = item.size || 0;
   const folder = item.folder || '';
   const div = document.createElement('div');
   div.className = 'xt-ms-item xt-ms-sample';
   const folderTag = (searchQuery && folder) ? '<span class="xt-ms-folder-tag">' + folder + '</span>' : '';
   div.innerHTML = '<span class="xt-ms-name" title="' + filename + '">' + filename + '</span>' + folderTag + '<span class="xt-ms-size">' + formatFileSize(size) + '</span>';
   div.draggable = true;
   div.addEventListener('dragstart', (e) => {
      e.dataTransfer.setData('text/plain', JSON.stringify({
         type: 'samplebank',
         uuid: item.uuid,
         hash: item.hash,
         filename: item.filename,
         name: item.filename,
      }));
      e.dataTransfer.effectAllowed = 'copy';
   });
   // Click to preview
   div.addEventListener('click', () => {
      document.querySelectorAll('.xt-ms-item').forEach(el => el.classList.remove('active'));
      div.classList.add('active');
      previewSample(item);
   });
   return div;
}

// --- Sample preview playback ---
let msPreviewAudio = null;
let msPreviewSource = null;

function previewSample(item) {
   if (msPreviewSource) { try { msPreviewSource.stop(); } catch (_) {} try { msPreviewSource.disconnect(); } catch (_) {} }
   const id = item.uuid || item.hash || item.filename;
   if (!id) return;
   const url = '/media/get/' + encodeURIComponent(id) + '?format=samplebank';
   fetch(url).then(r => r.arrayBuffer()).then(ab => {
      if (!msPreviewAudio) msPreviewAudio = new (window.AudioContext || window.webkitAudioContext)();
      if (msPreviewAudio.state === 'suspended') msPreviewAudio.resume();
      // Try native decode, fall back to AIFF parser
      msPreviewAudio.decodeAudioData(ab.slice(0)).then(buf => {
         msPreviewSource = msPreviewAudio.createBufferSource();
         msPreviewSource.buffer = buf;
         msPreviewSource.connect(msPreviewAudio.destination);
         msPreviewSource.start(0);
      }).catch(() => {
         // AIFF fallback (Chrome/Firefox don't support AIFF in decodeAudioData)
         if (isAIFF(ab)) {
            decodeAIFF(msPreviewAudio, ab).then(buf => {
               if (!buf) { console.warn('[xtracker] AIFF decode failed for', item.filename); return; }
               msPreviewSource = msPreviewAudio.createBufferSource();
               msPreviewSource.buffer = buf;
               msPreviewSource.connect(msPreviewAudio.destination);
               msPreviewSource.start(0);
            }).catch(e => console.warn('[xtracker] AIFF decode error for', item.filename, e));
         } else {
            console.warn('[xtracker] preview decode failed for', item.filename);
         }
      });
   }).catch(e => {
      console.warn('[xtracker] preview fetch failed for', item.filename, e);
   });
}

async function loadModuleFromServer(item) {
   const filename = item.filename || item.name || 'song.mod';
   const id = item.uuid || item.hash || item.filename || item.name;
   if (!id) return;
   const url = '/media/get/' + encodeURIComponent(id) + '?format=module';
   try {
      const resp = await fetch(url);
      if (!resp.ok) { console.warn('[xtracker] module fetch failed:', resp.status); return; }
      const ab = await resp.arrayBuffer();
      loadModuleFromArrayBuffer(ab, filename);
   } catch (err) {
      console.warn('[xtracker] module fetch error:', err);
   }
}

function wireModuleSearch() {
   if (dom.msSearch) {
      let t;
      dom.msSearch.addEventListener('input', () => { clearTimeout(t); t = setTimeout(fetchModuleList, 300); });
   }
   if (dom.msSortby) dom.msSortby.addEventListener('change', fetchModuleList);

   // Tab switching: MODULES / SAMPLES
   const tabs = document.querySelectorAll('.xt-ms-tab');
   tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
         msActiveTab = tab.dataset.msTab;
         msSampleFolder = null;
         tabs.forEach((t) => t.classList.remove('active'));
         tab.classList.add('active');
         if (dom.msSearch) dom.msSearch.value = '';
         if (dom.msSortby) dom.msSortby.style.display = (msActiveTab === 'samples') ? 'none' : '';
         fetchModuleList();
      });
   });

   fetchModuleList();
}

// ============================================================================
// SAVE / EXPORT
// ============================================================================
function saveModule() {
   const isXM = Tracker.inFTMode();
   const ext = isXM ? 'xm' : 'mod';
   const name = (Tracker.getFileName && Tracker.getFileName() || 'song') + '.' + ext;
   Editor.buildBinary(isXM ? MODULETYPE.xm : MODULETYPE.mod, (buffer) => {
      if (!buffer) { console.warn('[xtracker] buildBinary returned nothing'); return; }
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
   });
}

function exportWav() {
   if (!Audio.isRendering || !Audio.isRendering()) ensureAudio();
   const song = Tracker.getSong();
   if (!song) return;
   const bpm = Tracker.getBPM();
   const speed = Tracker.getAmigaSpeed();
   const tickTime = 2.5 / bpm;
   const patternLen = Tracker.getPatternLength();
   const songLen = song.length || song.patternTable.length;
   const totalSeconds = songLen * patternLen * speed * tickTime + 2;
   const sampleRate = 44100;
   const length = Math.ceil(totalSeconds * sampleRate);

   const modal = $('xt-export-modal');
   const fill = $('xt-export-progress-fill');
   const status = $('xt-export-status');
   const title = $('xt-export-title');
   modal.hidden = false;
   title.textContent = 'Rendering...';
   status.textContent = 'Preparing offline render...';
   fill.style.width = '0%';

   Audio.startRendering(length);
   Tracker.setCurrentSongPosition(0);
   Tracker.setCurrentPatternPos(0);
   Tracker.playSong();

   let elapsed = 0;
   const totalMs = totalSeconds * 1000;
   const timer = setInterval(() => {
      elapsed += 200;
      const pct = Math.min(99, (elapsed / totalMs) * 100);
      fill.style.width = pct + '%';
      status.textContent = Math.floor(pct) + '%';
      if (elapsed >= totalMs) {
         clearInterval(timer);
         Tracker.stop();
         Audio.stopRendering(async (wavBuffer) => {
            fill.style.width = '100%';
            status.textContent = 'Encoding WAV...';
            const wav = audioBufferToWav(wavBuffer);
            const blob = new Blob([wav], { type: 'audio/wav' });
            const fileName = (Tracker.getFileName && Tracker.getFileName() || 'xtracker-song') + '.wav';
            // Save to bucket
            status.textContent = 'Adding to bucket...';
            try {
               const arrayBuffer = await blob.arrayBuffer();
               if (bucket && bucket.add) {
                  await bucket.add({
                     source: 'xtracker',
                     name: fileName,
                     wavLike: arrayBuffer,
                     fileType: 'wav',
                  });
                  status.textContent = 'Added to bucket: ' + fileName;
               } else {
                  // Fallback: download
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = fileName;
                  a.click();
                  setTimeout(() => URL.revokeObjectURL(url), 1000);
                  status.textContent = 'Downloaded: ' + fileName;
               }
            } catch (e) {
               console.error('[export] add to bucket failed:', e);
               status.textContent = 'Error: ' + (e.message || 'unknown');
            }
            setTimeout(() => { modal.hidden = true; }, 1500);
         });
      }
   }, 200);
}

// ============================================================================
// BUCKET INTEGRATION
// ============================================================================
function initBucket() {
   const result = initLegacyBucket({
      appName: 'xtracker',
      bucketContentSelector: '.xtrc-bucket-panel .bucketcontent',
      bucketPanelSelector: '.xtrc-bucket-panel',
      onItemDrop: (item) => {
         // Dropped onto app area — try to load as module if it's a mod/xm
         if (item && item.name && /\.(mod|xm|it|s3m)$/i.test(item.name)) {
            let ab;
            if (item.data instanceof ArrayBuffer) ab = item.data;
            else if (item.data instanceof Uint8Array) ab = item.data.buffer;
            else if (item.wav instanceof ArrayBuffer) ab = item.wav;
            else if (item.wav instanceof Uint8Array) ab = item.wav.buffer;
            if (ab) loadModuleFromArrayBuffer(ab.slice(0), item.name);
         }
      },
   });
   bucket = result.bucket;
   exportModal = result.exportModal;
   window.exportModal = exportModal;
}

// ============================================================================
// SPLIT GRID
// ============================================================================
function initSplit() {
   const wrapper = document.getElementById('wrapper');
   if (!wrapper) return;
   // Outer gutter (panel-area | gutter | bucket): NOT handled by split-grid.
   // #wrapper's grid-template-columns uses var(--shared-bucket-width, 320px),
   // and split-grid's parser cannot handle var() (it returns null for that
   // token, crashing startDragging -> trackValues.filter). Instead we drive
   // the bucket width manually via the --shared-bucket-width CSS variable,
   // mirroring the xtrc/wavedit pattern.
   const outerGutter = wrapper.querySelector('.gutter-col-1');
   if (outerGutter) {
      const STORAGE_KEY = 'xtracker-bucket-width';
      const MIN_W = 200;
      const MAX_MARGIN = 240;
      const saved = parseInt(localStorage.getItem(STORAGE_KEY) || '', 10);
      if (Number.isFinite(saved) && saved >= MIN_W) {
         document.documentElement.style.setProperty('--shared-bucket-width', saved + 'px');
      }
      let dragging = false;
      const onMove = (e) => {
         if (!dragging) return;
         const vw = window.innerWidth;
         let w = vw - e.clientX;
         const maxW = Math.max(MIN_W, vw - MAX_MARGIN);
         w = Math.max(MIN_W, Math.min(maxW, w));
         document.documentElement.style.setProperty('--shared-bucket-width', w + 'px');
      };
      const onUp = () => {
         if (!dragging) return;
         dragging = false;
         outerGutter.classList.remove('active');
         document.body.style.cursor = '';
         document.body.style.userSelect = '';
         window.removeEventListener('mousemove', onMove);
         window.removeEventListener('mouseup', onUp);
         const cur = getComputedStyle(document.documentElement).getPropertyValue('--shared-bucket-width').trim();
         const px = parseInt(cur, 10);
         if (Number.isFinite(px)) localStorage.setItem(STORAGE_KEY, String(px));
      };
      outerGutter.addEventListener('mousedown', (e) => {
         e.preventDefault();
         dragging = true;
         outerGutter.classList.add('active');
         document.body.style.cursor = 'col-resize';
         document.body.style.userSelect = 'none';
         window.addEventListener('mousemove', onMove);
         window.addEventListener('mouseup', onUp);
      });
   }
   // Inner split: module-search | gutter | pattern-editor | gutter | right-panel
   const main = document.querySelector('.xt-main');
   if (main) {
      const msGutter = main.querySelector('.gutter-col-ms');
      const innerGutter = main.querySelector('.gutter-col-inner');
      try {
         Split({
            columnGutters: [
               { track: 1, element: msGutter },
               { track: 3, element: innerGutter },
            ],
            columnMinSizes: { 0: 140, 2: 200, 4: 180 },
            columnSnapOffset: 0,
         });
      } catch (e) { console.warn('[xtracker] split-grid inner column init failed:', e); }
   }
   // Row split: top-row (main) | gutter | bottom-panel
   const panelArea = document.querySelector('.panel-area');
   if (panelArea) {
      const rowGutter = panelArea.querySelector('.gutter-row-1');
      if (rowGutter) {
         try {
            Split({
               rowGutters: [{ track: 2, element: rowGutter }],
               rowMinSizes: { 0: 120, 2: 80 },
               rowSnapOffset: 0,
            });
         } catch (e) { console.warn('[xtracker] split-grid row init failed:', e); }
      }
   }
}

// ============================================================================
// TAB SWITCHING
// ============================================================================
function setBottomTab(target) {
   const btabs = document.querySelectorAll('.xt-bottom-tab');
   btabs.forEach(t => t.classList.toggle('active', t.dataset.btab === target));
   $('xt-bpanel-se').hidden = (target !== 'se');
   $('xt-bpanel-ie').hidden = (target !== 'ie');
   $('xt-bpanel-kb').hidden = (target !== 'kb');
   $('xt-bpanel-midi').hidden = (target !== 'midi');
   if (target === 'se') {
      const s = Tracker.getCurrentInstrument() && Tracker.getCurrentInstrument().sample;
      redrawSampleEditor();
   }
   if (target === 'ie') {
      requestAnimationFrame(() => { drawInstEditor('volume'); drawInstEditor('panning'); });
   }
   if (target === 'kb') renderPiano();
   if (target === 'midi') {
      midiPanel.renderRouting();
      initNumberSteppers(document.getElementById('xt-bpanel-midi'));
   }
}

function wireTabs() {
   const tabs = document.querySelectorAll('.xt-right-tab');
   tabs.forEach(tab => {
      tab.addEventListener('click', () => {
         const target = tab.dataset.tab;
         tabs.forEach(t => t.classList.toggle('active', t === tab));
         document.getElementById('xt-panel-instr').hidden = (target !== 'instr');
         document.getElementById('xt-panel-sample').hidden = (target !== 'sample');
         document.getElementById('xt-panel-song').hidden = (target !== 'song');
      });
   });
}

// ============================================================================
// BOTTOM TAB SWITCHING
// ============================================================================
function wireBottomTabs() {
   const tabs = document.querySelectorAll('.xt-bottom-tab');
   tabs.forEach(tab => {
      tab.addEventListener('click', () => setBottomTab(tab.dataset.btab));
   });
}

// ============================================================================
// QWERTY PIANO KEYBOARD
// ============================================================================
const PIANO_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const BLACK_KEYS = [1, 3, 6, 8, 10]; // C#, D#, F#, G#, A#

function renderPiano() {
   const piano = $('xt-kb-piano');
   if (!piano) return;
   piano.innerHTML = '';
   const octaves = 4; // show 4 octaves
   const baseOct = parseInt(($('xt-kb-octave') || {}).value || '4', 10) - 1;
   // White keys container
   const whiteWrap = document.createElement('div');
   whiteWrap.style.display = 'flex';
   whiteWrap.style.flex = '1 1 auto';
   whiteWrap.style.position = 'relative';
   whiteWrap.style.height = '100%';
   let whiteCount = 0;
   for (let oct = 0; oct < octaves; oct++) {
      for (let i = 0; i < 12; i++) {
         if (BLACK_KEYS.includes(i)) continue;
         const key = makePianoKey(baseOct + oct, i, false, whiteCount);
         whiteWrap.appendChild(key);
         whiteCount++;
      }
   }
   piano.appendChild(whiteWrap);
   // Black keys positioned absolutely over white keys
   whiteCount = 0;
   for (let oct = 0; oct < octaves; oct++) {
      for (let i = 0; i < 12; i++) {
         if (!BLACK_KEYS.includes(i)) { whiteCount++; continue; }
         const blackKey = makePianoKey(baseOct + oct, i, true, whiteCount - 1);
         // position: between previous white key and next
         const pct = (whiteCount / (octaves * 7)) * 100;
         blackKey.style.left = `calc(${pct}% - 12px)`;
         whiteWrap.appendChild(blackKey);
      }
   }
}

function makePianoKey(octave, noteInOctave, isBlack, whiteIndex) {
   const key = document.createElement('div');
   key.className = 'xt-key' + (isBlack ? ' xt-key-black' : '');
   const noteIndex = octave * 12 + noteInOctave + 1; // FTNotes 1-based
   const name = PIANO_NOTES[noteInOctave] + octave;
   key.dataset.note = noteIndex;
   key.dataset.name = name;
   const lbl = document.createElement('span');
   lbl.className = 'xt-key-label';
   lbl.textContent = name;
   key.appendChild(lbl);
   // Play on click (mousedown = note on, mouseup = note off)
   const playNote = (e) => {
      e.preventDefault();
      ensureAudio();
      key.classList.add('active');
      playPreviewNote(noteIndex);
      // Also place note at cursor in pattern
      putNoteAtCursor(noteIndex);
      setTimeout(() => key.classList.remove('active'), 120);
   };
   key.addEventListener('mousedown', playNote);
   return key;
}

function playPreviewNote(noteIndex) {
   if (!audioCtx) return;
   try {
      const t = audioCtx.currentTime;
      const inst = Tracker.getCurrentInstrument();
      if (inst && inst.play) inst.play(noteIndex, 0, 64, cursor.track, [], t);
   } catch (_) {}
}

// ============================================================================
// INSTRUMENT EDITOR (bottom tab)
// ============================================================================
function renderInstrumentEditor() {
   const inst = Tracker.getCurrentInstrument();
   if (!inst) {
      loadInstrument(null);
      return;
   }
   $('xt-ie-name').value = inst.name || '';
   $('xt-ie-fadeout').value = inst.fadeout != null ? inst.fadeout : 128;
   syncInstEditorUI();
   loadInstrument(inst);
}

function wireInstrumentEditor() {
   const syncInst = (el, prop, isInt) => () => {
      const inst = Tracker.getCurrentInstrument();
      if (!inst) return;
      inst[prop] = isInt ? parseInt(el.value, 10) || 0 : el.value;
      renderInstruments();
   };
   if ($('xt-ie-name')) $('xt-ie-name').addEventListener('change', syncInst($('xt-ie-name'), 'name', false));
   if ($('xt-ie-fadeout')) $('xt-ie-fadeout').addEventListener('change', syncInst($('xt-ie-fadeout'), 'fadeout', true));
   initInstrumentEditor();
   if ($('xt-kb-octave')) $('xt-kb-octave').addEventListener('change', renderPiano);
   const octUp = $('xt-kb-oct-up');
   const octDown = $('xt-kb-oct-down');
   if (octUp) octUp.addEventListener('click', () => {
      const el = $('xt-kb-octave');
      const o = parseInt(el.value, 10) || 4;
      if (o < 8) { el.value = o + 1; renderPiano(); }
   });
   if (octDown) octDown.addEventListener('click', () => {
      const el = $('xt-kb-octave');
      const o = parseInt(el.value, 10) || 4;
      if (o > 0) { el.value = o - 1; renderPiano(); }
   });
}

// ============================================================================
// RENDER ALL
// ============================================================================
function renderAll() {
   renderPattern();
   renderInstruments();
   renderSampleSlots();
   renderSongSeq();
   renderSampleEditor();
   renderInstrumentEditor();
   updateTransport();
}

// ============================================================================
// NUMBER INPUT STEPPERS — auto-wrap all number inputs with +/- buttons
// ============================================================================
function initNumberSteppers(root) {
   const scope = root || document;
   const inputs = scope.querySelectorAll('input[type="number"].xt-num, input[type="number"].xt-num-sm, input[type="number"].xt-mb-num');
   inputs.forEach(input => {
      if (input.parentElement && input.parentElement.classList.contains('xt-stepper')) return;
      const min = parseFloat(input.min);
      const max = parseFloat(input.max);
      const step = parseFloat(input.step) || 1;

      const wrapper = document.createElement('div');
      wrapper.className = 'xt-stepper';

      const upBtn = document.createElement('button');
      upBtn.className = 'xt-stepper-btn xt-step-up';
      upBtn.textContent = '+';
      upBtn.tabIndex = -1;
      upBtn.type = 'button';

      const downBtn = document.createElement('button');
      downBtn.className = 'xt-stepper-btn xt-step-down';
      downBtn.textContent = '-';
      downBtn.tabIndex = -1;
      downBtn.type = 'button';

      input.parentNode.insertBefore(wrapper, input);
      wrapper.appendChild(downBtn);
      wrapper.appendChild(input);
      wrapper.appendChild(upBtn);

      const clamp = (v) => {
         if (!isNaN(min)) v = Math.max(min, v);
         if (!isNaN(max)) v = Math.min(max, v);
         return v;
      };

      upBtn.addEventListener('click', (e) => {
         e.preventDefault();
         input.value = clamp((parseFloat(input.value) || 0) + step);
         input.dispatchEvent(new Event('change', { bubbles: true }));
         input.dispatchEvent(new Event('input', { bubbles: true }));
      });

      downBtn.addEventListener('click', (e) => {
         e.preventDefault();
         input.value = clamp((parseFloat(input.value) || 0) - step);
         input.dispatchEvent(new Event('change', { bubbles: true }));
         input.dispatchEvent(new Event('input', { bubbles: true }));
      });
   });
}

// ============================================================================
// INIT
// ============================================================================
function init() {
   initEngine();
   wireEngineEvents();
   Tracker.new();
   wireTransport();
   wirePatternEditor();
   wireKeyboard();
   wireSampleEditor();
   initSampleEditor(audioCtx);
   wireLeftPanel();
   wireFileDrop();
   wireModuleSearch();
   wireTabs();
   wireBottomTabs();
   wireInstrumentEditor();
   initBucket();
   initSplit();
   initNumberSteppers();
   // MIDI panel — fails silently if Web MIDI not supported
   midiPanel.init({
      onNoteOn: (ftNote, vel, ch) => {
         if (recordMode || editMode) putNoteAtCursor(ftNote);
         else playPreviewNote(ftNote);
      },
      onNoteOff: (ftNote, ch) => {},
      getTrackCount: () => Tracker.getTrackCount(),
      getCurrentInstrumentIndex: () => Tracker.getCurrentInstrumentIndex(),
   });
   midiPanel.wireControls();
   // Enable edit mode by default so users can enter notes immediately
   setEditMode(true);
   renderAll();

   // Mobile warning close
   const closeMobile = $('close-mobile-warning');
   if (closeMobile) closeMobile.addEventListener('click', () => {
      $('mobile-warning-modal').classList.add('hidden');
   });

   // Redraw waveform on resize
   window.addEventListener('resize', () => {
      const s = Tracker.getCurrentInstrument() && Tracker.getCurrentInstrument().sample;
      redrawSampleEditor();
   });

   console.log('[xtracker] initialized — engine:', !!Tracker, 'audio:', !!Audio);
}

// Start when DOM is ready
if (document.readyState === 'loading') {
   document.addEventListener('DOMContentLoaded', init);
} else {
   init();
}
